/**
 * CRM operations view — Phase 2.2.c.2 (CRM installations sub-cluster).
 *
 * Owns the entire installations workflow that previously lived inline in
 * admin.js: list + filters + pagination + dashboard, the create/validate/
 * payment/refund/equipment/pole/auto-provision/ticket dialogs, the detail
 * panel, the bulk pole/note printers, and the helper that primes the
 * technician-roster select used by the assign-tech dialog.
 *
 * Plan-cache helpers (`ensureCrmPlanCache`, `loadCrmPlansForSelect`,
 * `getCrmPlanLabel`) also live here because the create/edit/validate forms
 * are the only callers; if a future cluster needs them they can be lifted
 * to a shared module.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `formatDateTimeShort`, `hasRole`, `openDialog`,
 *     `closeDialog`, `formatCrmLabel`, `crmStatusClass`,
 *     `ensureSupervisorUsersLoaded`, `adminUserSortKey`,
 *     `primaryAdminUserName`, `getSupervisorAdminUsers`,
 *     `openTicketFromNotification`.
 *   - Technician-roster shared flags exposed via
 *     `getTechnicianRosterState` / `setTechnicianRosterState` so the cache
 *     stays consistent with `ensureTechnicianRosterLoaded` over in
 *     admin.js.
 *
 * Phase 3.2.c: replaced `configure({helpers})` injection with direct
 * imports. Cross-view formatters come from sibling view modules (crm.js)
 * via real ES imports; admin-roster state is shared via
 * `src/lib/admin-roster.js`.
 */

import { escapeHtml, formatDateTimeShort, formatDate, formatCurrency } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';
import { adminUserSortKey, primaryAdminUserName } from '../../src/lib/ticket-utils.js';
import {
  ensureSupervisorUsersLoaded, getSupervisorAdminUsers,
  getTechnicianRosterState, setTechnicianRosterState,
} from '../../src/lib/admin-roster.js';
import { formatCrmLabel, crmStatusClass } from './crm.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

// `openTicketFromNotification` lives on the tickets list sub-view; reach
// it lazily so we don't form a hard dependency on tickets.js's parse order.
const openTicketFromNotification = (id) => {
  const list = window.adminViews && window.adminViews.tickets && window.adminViews.tickets.list;
  if (list && typeof list.openFromNotification === 'function') list.openFromNotification(id);
};

  // ---- Compat aliases (so lifted function bodies need zero changes) ----
  const adminAPI = api;
  const API = api.routes;
  const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) -----------------------------------------
  let crmInstallationsView, crmInstallationsSearch, crmInstallationsStatusFilter,
      crmInstallationsCustomerFilter, crmInstallationsAgeFilter, crmInstallationsExpectedFilter,
      crmInstallationsOpenReports, crmInstallationsExport, crmInstallationsBulkDispatch,
      crmInstallationsBulkPole, crmInstallationsBulkNote, crmInstallationsNewBtn,
      crmInstallationsSelectAll, crmInstallationsTbody, crmInstallationsPageInfo,
      crmInstallationsPrev, crmInstallationsNext, crmInstallationsDashboard,
      crmInstallationsPending, crmInstallationsOverdue, crmInstallationsDueWeek,
      crmInstallationsCompleted, crmInstallationsStatusStrip,
      crmInstallationDetail, crmInstallDetailHome, crmInstallationDetailEmpty, crmInstallationDetailBody,
      crmInstallationDetailName, crmInstallationDetailMeta, crmInstallationDetailBreadcrumb, crmInstallationDetailGrid,
      crmInstallationDetailActions, crmInstallationPayments,
      crmInstallationFlagDetails, crmInstallationFlagDetailsBody,
      crmInstallationDialog, crmInstallationForm, crmInstallationSubmit,
      crmInstallCustomerType, crmInstallTechnology, crmInstallType,
      crmInstallOptCpe, crmInstallOptDropCable, crmInstallOptCables,
      crmInstallOptClips, crmInstallOptPole, crmInstallOptOnt, crmInstallOptWifi6,
      crmInstallOntTypeRouter, crmInstallOntTypeModem,
      crmInstallRouterGroup, crmInstallRouterAx, crmInstallRouterSmall, crmInstallRouterOwned,
      crmInstallPackage, crmInstallAmountInstall, crmInstallAmountSubscription,
      crmInstallSubscriptionField, crmInstallReceiptInstall, crmInstallReceiptSubscription,
      crmInstallSubscriptionReceiptField, crmInstallAccessPoints,
      crmInstallContactName, crmInstallContactPhoneCode, crmInstallContactPhoneNumber,
      crmInstallSecondaryPhoneCode, crmInstallSecondaryPhoneNumber, crmInstallContactEmail,
      crmInstallAddress, crmInstallCity, crmInstallRegion, crmInstallNotes,
      crmInstallValidateDialog, crmInstallValidateForm,
      crmInstallDetailsDialog, crmInstallDetailsForm, crmInstallDetailsMaterials,
      crmInstallDetailsPayments, crmInstallDetailsCustomerType, crmInstallDetailsTechnology,
      crmInstallDetailsType, crmInstallDetailsPackage, crmInstallDetailsExpected,
      crmInstallDetailsAccessPoints, crmInstallDetailsContactName,
      crmInstallDetailsContactPhoneCode, crmInstallDetailsContactPhoneNumber,
      crmInstallDetailsSecondaryPhoneCode, crmInstallDetailsSecondaryPhoneNumber,
      crmInstallDetailsContactEmail, crmInstallDetailsStreet, crmInstallDetailsCity,
      crmInstallDetailsRegion, crmInstallDetailsNotes, crmInstallDetailsSave,
      crmInstallDetailsComponents, crmInstallDetailsTechWarn,
      crmValidateMaterialsList, crmValidatePaymentsList,
      crmValidateFirstName, crmValidateLastName, crmValidatePhone, crmValidateEmail,
      crmValidateStreet, crmValidateCity, crmValidateRegion, crmValidateExpected, crmValidatePackage,
      crmValidateChecklist, crmValidateSubmitBtn, crmValidateCheckTech, crmValidateCheckClass,
      crmInstallFlagDialog, crmInstallFlagForm, crmFlagReason,
      crmInstallRefundDialog, crmInstallRefundForm, crmInstallRefundReason,
      crmInstallPaymentDialog, crmInstallPaymentForm, crmPaymentAmount, crmPaymentReceipt,
      crmPaymentItemsList, crmPaymentAddItem, crmPaymentItemsTotal,
      crmInstallPoleDialog, crmInstallPoleForm, crmPoleBulkList,
      crmInstallEquipmentDialog, crmInstallEquipmentForm,
      crmEquipIccidTail, crmEquipSim, crmEquipRouter, crmEquipOnt, crmEquipCpe,
      crmEquipAccessoriesList, crmEquipAddAccessory,
      crmInstallTicketDialog, crmInstallTicketForm, crmInstallTicketSupervisor,
      crmInstallAssignTechDialog, crmInstallAssignTechForm, crmAssignTechnicianSelect,
      crmInstallAutoProvisionDialog, crmInstallAutoProvisionForm,
      openCrmInstallReport,
      crmAutoPaymentsList, crmAutoInstallTotal, crmAutoSubscriptionTotal,
      crmAutoPaymentsNote, crmAutoEmailWrap, crmAutoEmail, crmAutoSimWrap,
      crmAutoSim, crmAutoDidWrap, crmAutoDid, crmAutoDidFetch, crmAutoOverridesNote,
      crmAlertOverlay, crmAlertStack;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    crmInstallationsView = document.getElementById('crmInstallationsView');
    crmInstallationsSearch = document.getElementById('crmInstallationsSearch');
    crmInstallationsStatusFilter = document.getElementById('crmInstallationsStatusFilter');
    crmInstallationsCustomerFilter = document.getElementById('crmInstallationsCustomerFilter');
    crmInstallationsAgeFilter = document.getElementById('crmInstallationsAgeFilter');
    crmInstallationsExpectedFilter = document.getElementById('crmInstallationsExpectedFilter');
    crmInstallationsOpenReports = document.getElementById('crmInstallationsOpenReports');
    crmInstallationsExport = document.getElementById('crmInstallationsExport');
    crmInstallationsBulkDispatch = document.getElementById('crmInstallationsBulkDispatch');
    crmInstallationsBulkPole = document.getElementById('crmInstallationsBulkPole');
    crmInstallationsBulkNote = document.getElementById('crmInstallationsBulkNote');
    crmInstallationsNewBtn = document.getElementById('crmInstallationsNewBtn');
    crmInstallationsSelectAll = document.getElementById('crmInstallationsSelectAll');
    crmInstallationsTbody = document.getElementById('crmInstallationsTbody');
    crmInstallationsPageInfo = document.getElementById('crmInstallationsPageInfo');
    crmInstallationsPrev = document.getElementById('crmInstallationsPrev');
    crmInstallationsNext = document.getElementById('crmInstallationsNext');
    crmInstallationsDashboard = document.getElementById('crmInstallationsDashboard');
    crmInstallationsPending = document.getElementById('crmInstallationsPending');
    crmInstallationsOverdue = document.getElementById('crmInstallationsOverdue');
    crmInstallationsDueWeek = document.getElementById('crmInstallationsDueWeek');
    crmInstallationsCompleted = document.getElementById('crmInstallationsCompleted');
    crmInstallationsStatusStrip = document.getElementById('crmInstallationsStatusStrip');
    crmInstallationDetail = document.getElementById('crmInstallationDetail');
    // Home for the detail panel while collapsed. Clicking "Open" relocates this
    // node into an expander row directly under the clicked list row (inline), and
    // "Close" moves it back here (hidden) — so there is no separate panel to scroll
    // to at the bottom of a long list.
    if (crmInstallationDetail) {
      crmInstallDetailHome = crmInstallationDetail.parentNode;
      crmInstallationDetail.hidden = true;
    }
    crmInstallationDetailEmpty = document.getElementById('crmInstallationDetailEmpty');
    crmInstallationDetailBody = document.getElementById('crmInstallationDetailBody');
    crmInstallationDetailName = document.getElementById('crmInstallationDetailName');
    crmInstallationDetailMeta = document.getElementById('crmInstallationDetailMeta');
    crmInstallationDetailBreadcrumb = document.getElementById('crmInstallationDetailBreadcrumb');
    crmInstallationDetailGrid = document.getElementById('crmInstallationDetailGrid');
    crmInstallationDetailActions = document.getElementById('crmInstallationDetailActions');
    crmInstallationPayments = document.getElementById('crmInstallationPayments');
    crmInstallationFlagDetails = document.getElementById('crmInstallationFlagDetails');
    crmInstallationFlagDetailsBody = document.getElementById('crmInstallationFlagDetailsBody');
    crmInstallationDialog = document.getElementById('crmInstallationDlg');
    crmInstallationForm = document.getElementById('crmInstallationForm');
    crmInstallationSubmit = document.getElementById('crmInstallationSubmit');
    crmInstallCustomerType = document.getElementById('crmInstallCustomerType');
    crmInstallTechnology = document.getElementById('crmInstallTechnology');
    crmInstallType = document.getElementById('crmInstallType');
    crmInstallOptCpe = document.getElementById('crmInstallOptCpe');
    crmInstallOptDropCable = document.getElementById('crmInstallOptDropCable');
    crmInstallOptCables = document.getElementById('crmInstallOptCables');
    crmInstallOptClips = document.getElementById('crmInstallOptClips');
    crmInstallOptPole = document.getElementById('crmInstallOptPole');
    crmInstallOptOnt = document.getElementById('crmInstallOptOnt');
    crmInstallOptWifi6 = document.getElementById('crmInstallOptWifi6');
    crmInstallOntTypeRouter = document.getElementById('crmInstallOntTypeRouter');
    crmInstallOntTypeModem = document.getElementById('crmInstallOntTypeModem');
    crmInstallRouterGroup = document.getElementById('crmInstallRouterGroup');
    crmInstallRouterAx = document.getElementById('crmInstallRouterAx');
    crmInstallRouterSmall = document.getElementById('crmInstallRouterSmall');
    crmInstallRouterOwned = document.getElementById('crmInstallRouterOwned');
    crmInstallPackage = document.getElementById('crmInstallPackage');
    crmInstallAmountInstall = document.getElementById('crmInstallAmountInstall');
    crmInstallAmountSubscription = document.getElementById('crmInstallAmountSubscription');
    crmInstallSubscriptionField = document.getElementById('crmInstallSubscriptionField');
    crmInstallReceiptInstall = document.getElementById('crmInstallReceiptInstall');
    crmInstallReceiptSubscription = document.getElementById('crmInstallReceiptSubscription');
    crmInstallSubscriptionReceiptField = document.getElementById('crmInstallSubscriptionReceiptField');
    crmInstallAccessPoints = document.getElementById('crmInstallAccessPoints');
    crmInstallContactName = document.getElementById('crmInstallContactName');
    crmInstallContactPhoneCode = document.getElementById('crmInstallContactPhoneCode');
    crmInstallContactPhoneNumber = document.getElementById('crmInstallContactPhoneNumber');
    crmInstallSecondaryPhoneCode = document.getElementById('crmInstallSecondaryPhoneCode');
    crmInstallSecondaryPhoneNumber = document.getElementById('crmInstallSecondaryPhoneNumber');
    crmInstallContactEmail = document.getElementById('crmInstallContactEmail');
    crmInstallAddress = document.getElementById('crmInstallAddress');
    crmInstallCity = document.getElementById('crmInstallCity');
    crmInstallRegion = document.getElementById('crmInstallRegion');
    crmInstallNotes = document.getElementById('crmInstallNotes');
    crmInstallValidateDialog = document.getElementById('crmInstallValidateDlg');
    crmInstallValidateForm = document.getElementById('crmInstallValidateForm');
    crmInstallDetailsDialog = document.getElementById('crmInstallDetailsDlg');
    crmInstallDetailsForm = document.getElementById('crmInstallDetailsForm');
    crmInstallDetailsMaterials = document.getElementById('crmInstallDetailsMaterials');
    crmInstallDetailsComponents = document.getElementById('crmInstallDetailsComponents');
    crmInstallDetailsTechWarn = document.getElementById('crmInstallDetailsTechWarn');
    crmInstallDetailsPayments = document.getElementById('crmInstallDetailsPayments');
    crmInstallDetailsCustomerType = document.getElementById('crmInstallDetailsCustomerType');
    crmInstallDetailsTechnology = document.getElementById('crmInstallDetailsTechnology');
    crmInstallDetailsType = document.getElementById('crmInstallDetailsType');
    crmInstallDetailsPackage = document.getElementById('crmInstallDetailsPackage');
    crmInstallDetailsExpected = document.getElementById('crmInstallDetailsExpected');
    crmInstallDetailsAccessPoints = document.getElementById('crmInstallDetailsAccessPoints');
    crmInstallDetailsContactName = document.getElementById('crmInstallDetailsContactName');
    crmInstallDetailsContactPhoneCode = document.getElementById('crmInstallDetailsContactPhoneCode');
    crmInstallDetailsContactPhoneNumber = document.getElementById('crmInstallDetailsContactPhoneNumber');
    crmInstallDetailsSecondaryPhoneCode = document.getElementById('crmInstallDetailsSecondaryPhoneCode');
    crmInstallDetailsSecondaryPhoneNumber = document.getElementById('crmInstallDetailsSecondaryPhoneNumber');
    crmInstallDetailsContactEmail = document.getElementById('crmInstallDetailsContactEmail');
    crmInstallDetailsStreet = document.getElementById('crmInstallDetailsStreet');
    crmInstallDetailsCity = document.getElementById('crmInstallDetailsCity');
    crmInstallDetailsRegion = document.getElementById('crmInstallDetailsRegion');
    crmInstallDetailsNotes = document.getElementById('crmInstallDetailsNotes');
    crmInstallDetailsSave = document.getElementById('crmInstallDetailsSave');
    crmValidateMaterialsList = document.getElementById('crmValidateMaterialsList');
    crmValidatePaymentsList = document.getElementById('crmValidatePaymentsList');
    crmValidateFirstName = document.getElementById('crmValidateFirstName');
    crmValidateLastName = document.getElementById('crmValidateLastName');
    crmValidatePhone = document.getElementById('crmValidatePhone');
    crmValidateEmail = document.getElementById('crmValidateEmail');
    crmValidateStreet = document.getElementById('crmValidateStreet');
    crmValidateCity = document.getElementById('crmValidateCity');
    crmValidateRegion = document.getElementById('crmValidateRegion');
    crmValidateExpected = document.getElementById('crmValidateExpected');
    crmValidatePackage = document.getElementById('crmValidatePackage');
    crmValidateChecklist = document.getElementById('crmValidateChecklist');
    crmValidateSubmitBtn = document.getElementById('crmValidateSubmitBtn');
    crmValidateCheckTech = document.getElementById('crmValidateCheckTech');
    crmValidateCheckClass = document.getElementById('crmValidateCheckClass');
    if (crmValidateChecklist) {
      crmValidateChecklist.addEventListener('change', syncCrmValidateChecklist);
    }
    crmInstallFlagDialog = document.getElementById('crmInstallFlagDlg');
    crmInstallFlagForm = document.getElementById('crmInstallFlagForm');
    crmFlagReason = document.getElementById('crmFlagReason');
    crmInstallRefundDialog = document.getElementById('crmInstallRefundDlg');
    crmInstallRefundForm = document.getElementById('crmInstallRefundForm');
    crmInstallRefundReason = document.getElementById('crmInstallRefundReason');
    crmInstallPaymentDialog = document.getElementById('crmInstallPaymentDlg');
    crmInstallPaymentForm = document.getElementById('crmInstallPaymentForm');
    crmPaymentAmount = document.getElementById('crmPaymentAmount');
    crmPaymentReceipt = document.getElementById('crmPaymentReceipt');
    crmPaymentItemsList = document.getElementById('crmPaymentItemsList');
    crmPaymentAddItem = document.getElementById('crmPaymentAddItem');
    crmPaymentItemsTotal = document.getElementById('crmPaymentItemsTotal');
    crmInstallPoleDialog = document.getElementById('crmInstallPoleDlg');
    crmInstallPoleForm = document.getElementById('crmInstallPoleForm');
    crmPoleBulkList = document.getElementById('crmPoleBulkList');
    crmInstallEquipmentDialog = document.getElementById('crmInstallEquipmentDlg');
    crmInstallEquipmentForm = document.getElementById('crmInstallEquipmentForm');
    crmEquipIccidTail = document.getElementById('crmEquipIccidTail');
    crmEquipSim = document.getElementById('crmEquipSim');
    crmEquipRouter = document.getElementById('crmEquipRouter');
    crmEquipOnt = document.getElementById('crmEquipOnt');
    crmEquipCpe = document.getElementById('crmEquipCpe');
    crmEquipAccessoriesList = document.getElementById('crmEquipAccessoriesList');
    crmEquipAddAccessory = document.getElementById('crmEquipAddAccessory');
    crmInstallTicketDialog = document.getElementById('crmInstallTicketDlg');
    crmInstallTicketForm = document.getElementById('crmInstallTicketForm');
    crmInstallTicketSupervisor = document.getElementById('crmInstallTicketSupervisor');
    crmInstallAssignTechDialog = document.getElementById('crmInstallAssignTechDlg');
    crmInstallAssignTechForm = document.getElementById('crmInstallAssignTechForm');
    crmAssignTechnicianSelect = document.getElementById('crmAssignTechnicianSelect');
    crmInstallAutoProvisionDialog = document.getElementById('crmInstallAutoProvisionDlg');
    crmInstallAutoProvisionForm = document.getElementById('crmInstallAutoProvisionForm');
    openCrmInstallReport = document.getElementById('openCrmInstallReport');
    crmAutoPaymentsList = document.getElementById('crmAutoPaymentsList');
    crmAutoInstallTotal = document.getElementById('crmAutoInstallTotal');
    crmAutoSubscriptionTotal = document.getElementById('crmAutoSubscriptionTotal');
    crmAutoPaymentsNote = document.getElementById('crmAutoPaymentsNote');
    crmAutoEmailWrap = document.getElementById('crmAutoEmailWrap');
    crmAutoEmail = document.getElementById('crmAutoEmail');
    crmAutoSimWrap = document.getElementById('crmAutoSimWrap');
    crmAutoSim = document.getElementById('crmAutoSim');
    crmAutoDidWrap = document.getElementById('crmAutoDidWrap');
    crmAutoDid = document.getElementById('crmAutoDid');
    crmAutoDidFetch = document.getElementById('crmAutoDidFetch');
    crmAutoOverridesNote = document.getElementById('crmAutoOverridesNote');
    crmAlertOverlay = document.getElementById('crmAlertOverlay');
    crmAlertStack = document.getElementById('crmAlertStack');
  }

  // ---- State ------------------------------------------------------------
  const CRM_INSTALL_PAGE_SIZE = 10;
  const CRM_INSTALL_ADDRESS_LIMIT = 41;
  const crmInstallationsState = {
    items: [],
    total: 0,
    offset: 0,
    search: '',
    status: '',
    customerType: '',
    ageMin: '',
    expectedBefore: '',
    loading: false,
    selectedId: null,
    selectedDetail: null,
    selectedIds: new Set(),
    openRowId: null,          // installation currently expanded inline in the list
    lastExportJobId: null,
  };
  let crmBulkPoleDispatch = false;
  let crmInstallationsInitialized = false;
  // Sales prospect linked into the create dialog (credited on installation create).
  let crmInstallSelectedProspectId = null;
  let crmInstallProspectSearchTimer = null;

  function _clearInstallProspectLink() {
    crmInstallSelectedProspectId = null;
    const linked = document.getElementById('crmInstallProspectLinked');
    const results = document.getElementById('crmInstallProspectResults');
    const search = document.getElementById('crmInstallProspectSearch');
    if (linked) { linked.style.display = 'none'; linked.innerHTML = ''; }
    if (results) { results.style.display = 'none'; results.innerHTML = ''; }
    if (search) search.value = '';
  }

  function _selectInstallProspect(p) {
    crmInstallSelectedProspectId = p.id;
    const results = document.getElementById('crmInstallProspectResults');
    const linked = document.getElementById('crmInstallProspectLinked');
    const search = document.getElementById('crmInstallProspectSearch');
    if (results) { results.style.display = 'none'; results.innerHTML = ''; }
    if (search) search.value = '';
    // Prefill empty contact fields from the prospect.
    const name = document.getElementById('crmInstallContactName');
    const email = document.getElementById('crmInstallContactEmail');
    const phone = document.getElementById('crmInstallContactPhoneNumber');
    if (name && !name.value && p.full_name) name.value = p.full_name;
    if (email && !email.value && p.email) email.value = p.email;
    if (phone && !phone.value && p.phone_number) phone.value = String(p.phone_number).replace(/^\+?220/, '');
    if (linked) {
      const owner = p.owner_name ? `sales: ${p.owner_name}` : (p.owner_user_id ? `sales #${p.owner_user_id}` : 'unassigned');
      linked.innerHTML = '';
      const span = document.createElement('span');
      span.textContent = `🔗 Linked: ${p.full_name} — ${owner}`;
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'btn ghost small';
      clear.textContent = 'Unlink';
      clear.style.marginLeft = '8px';
      clear.addEventListener('click', _clearInstallProspectLink);
      linked.appendChild(span);
      linked.appendChild(clear);
      linked.style.display = '';
    }
  }

  async function runInstallProspectSearch() {
    const search = document.getElementById('crmInstallProspectSearch');
    const results = document.getElementById('crmInstallProspectResults');
    if (!search || !results) return;
    const q = search.value.trim();
    if (q.length < 2) { results.style.display = 'none'; results.innerHTML = ''; return; }
    try {
      const data = await adminAPI.fetch(API.crmProspectSearch(q, 10), { method: 'GET' });
      const items = Array.isArray(data) ? data : [];
      results.innerHTML = '';
      if (!items.length) {
        results.innerHTML = '<div style="padding:8px 10px; color:var(--sub); font-size:12px;">No matching prospects</div>';
        results.style.display = '';
        return;
      }
      items.forEach((p) => {
        const row = document.createElement('div');
        row.style.cssText = 'padding:8px 10px; cursor:pointer; border-bottom:1px solid var(--border,#f0f0f0);';
        row.addEventListener('mouseenter', () => { row.style.background = 'var(--hover,#f5f5f5)'; });
        row.addEventListener('mouseleave', () => { row.style.background = ''; });
        const owner = p.owner_name ? p.owner_name : (p.owner_user_id ? `#${p.owner_user_id}` : 'unassigned');
        const ready = p.install_ready ? ' ✅' : '';
        row.innerHTML = `<strong>${escapeHtml(p.full_name || '')}</strong>${ready}`
          + `<div style="font-size:11px; color:var(--sub);">${escapeHtml(p.phone_number || '—')} · sales: ${escapeHtml(owner)}</div>`;
        row.addEventListener('click', () => _selectInstallProspect(p));
        results.appendChild(row);
      });
      results.style.display = '';
    } catch (err) {
      results.style.display = 'none';
    }
  }
  const crmPlanCache = new Map();

  // ---- Plan cache + form helpers ---------------------------------------
  async function ensureCrmPlanCache(customerType) {
    const key = customerType || 'all';
    if (crmPlanCache.has(key)) return;
    const params = new URLSearchParams();
    if (customerType) params.set('customer_type', customerType);
    params.set('active_only', 'true');
    const data = await adminAPI.fetch(API.crmConfigProducts(params.toString()), { method: 'GET' });
    const items = Array.isArray(data?.items) ? data.items : [];
    crmPlanCache.set(key, items);
  }

  function renderCrmPlanOptionsForSelect(selectEl, items, tech) {
    if (!selectEl) return;
    const filtered = filterPlansByTechnology(items, tech);
    const list = tech ? filtered : items;
    selectEl.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = list.length
      ? 'Select plan'
      : (items.length ? `No plans for ${tech || 'selected technology'}` : 'No plans synced');
    selectEl.appendChild(placeholder);
    list.forEach((item) => {
      const option = document.createElement('option');
      option.value = item.porta_product_code;
      option.textContent = item.label
        ? `${item.label} (${item.porta_product_code})`
        : item.porta_product_code;
      selectEl.appendChild(option);
    });
  }

  async function loadCrmPlansForSelect(selectEl, customerType, tech) {
    if (!selectEl) return;
    const key = customerType || 'all';
    selectEl.innerHTML = '<option value="">Loading plans…</option>';
    try {
      await ensureCrmPlanCache(customerType);
      const items = crmPlanCache.get(key) || [];
      renderCrmPlanOptionsForSelect(selectEl, items, tech);
    } catch (err) {
      selectEl.innerHTML = '<option value="">Failed to load plans</option>';
      showFlash(err?.message || 'Failed to load plans', 'error');
    }
  }

  function getCrmPlanLabel(code, customerType) {
    if (!code) return null;
    const keys = [];
    if (customerType && crmPlanCache.has(customerType)) keys.push(customerType);
    if (crmPlanCache.has('all')) keys.push('all');
    for (const key of keys) {
      const found = (crmPlanCache.get(key) || []).find((item) => item.porta_product_code === code);
      if (found) return found.label || code;
    }
    for (const items of crmPlanCache.values()) {
      const found = (items || []).find((item) => item.porta_product_code === code);
      if (found) return found.label || code;
    }
    return code;
  }

  function splitFullName(value) {
    const name = String(value || '').trim();
    if (!name) return { first: '', last: '' };
    const parts = name.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  }

  function extractAddressParts(detail) {
    const meta = detail && (detail.meta || detail.metadata) ? (detail.meta || detail.metadata) : {};
    const metaParts = meta && meta.address_parts ? meta.address_parts : null;
    if (metaParts) {
      return {
        street: metaParts.address || '',
        city: metaParts.city || '',
        region: metaParts.region || '',
      };
    }
    const address = String(detail?.address || '').trim();
    if (!address) return { street: '', city: '', region: '' };
    const parts = address.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 3) {
      return { street: parts[0], city: parts[1], region: parts.slice(2).join(', ') };
    }
    if (parts.length === 2) {
      return { street: parts[0], city: parts[1], region: '' };
    }
    return { street: parts[0], city: '', region: '' };
  }

  function validateCrmInstallAddress(street, city, region) {
    const parts = [street, city, region].filter((val) => val);
    if (!parts.length) return { error: null, address: '', parts };
    if (region && !/[A-Za-z]/.test(region)) {
      return { error: 'Region must include letters (not only numbers).', address: parts.join(', '), parts };
    }
    const address = parts.join(', ');
    if (address.length > CRM_INSTALL_ADDRESS_LIMIT) {
      return {
        error: `Address (street, city, region) must be ${CRM_INSTALL_ADDRESS_LIMIT} characters or fewer.`,
        address,
        parts,
      };
    }
    return { error: null, address, parts };
  }

  function prefillCrmValidateForm(detail) {
    const nameParts = splitFullName(detail?.contact_name);
    if (crmValidateFirstName) crmValidateFirstName.value = nameParts.first;
    if (crmValidateLastName) crmValidateLastName.value = nameParts.last;
    if (crmValidatePhone) crmValidatePhone.value = detail?.contact_phone || '';
    if (crmValidateEmail) crmValidateEmail.value = detail?.contact_email || '';
    const addressParts = extractAddressParts(detail);
    if (crmValidateStreet) crmValidateStreet.value = addressParts.street;
    if (crmValidateCity) crmValidateCity.value = addressParts.city;
    if (crmValidateRegion) crmValidateRegion.value = addressParts.region;
    if (crmValidateExpected) {
      crmValidateExpected.value = detail?.expected_install_date
        ? String(detail.expected_install_date).split('T')[0]
        : '';
    }
  }

  // The pre-validation checklist: every box must be ticked before the Validate
  // button is enabled. Reset it (all unchecked) each time the dialog opens, and
  // surface the known technology / customer class so the operator can confirm
  // against real data rather than from memory.
  function crmValidateChecks() {
    return crmValidateChecklist
      ? Array.from(crmValidateChecklist.querySelectorAll('[data-validate-check]'))
      : [];
  }

  function crmValidateChecklistComplete() {
    const checks = crmValidateChecks();
    return checks.length > 0 && checks.every((box) => box.checked);
  }

  function syncCrmValidateChecklist() {
    if (crmValidateSubmitBtn) crmValidateSubmitBtn.disabled = !crmValidateChecklistComplete();
  }

  function resetCrmValidateChecklist(detail) {
    crmValidateChecks().forEach((box) => { box.checked = false; });
    if (crmValidateCheckTech) {
      const tech = detail && detail.technology_type ? String(detail.technology_type) : '';
      crmValidateCheckTech.textContent = tech ? `on file: ${tech}` : '';
    }
    if (crmValidateCheckClass) {
      const cls = detail && detail.customer_type ? String(detail.customer_type) : '';
      crmValidateCheckClass.textContent = cls ? `on file: ${cls}` : '';
    }
    syncCrmValidateChecklist();
  }

  function addCrmAccessoryRow(item = {}) {
    if (!crmEquipAccessoriesList) return;
    const row = document.createElement('div');
    row.className = 'crm-accessory-item-row';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Pick an accessory from inventory';
    nameInput.className = 'crm-accessory-item-name';
    nameInput.setAttribute('list', 'crmEquipAccessoryList');
    if (item.name) nameInput.value = item.name;
    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.min = '0';
    qtyInput.step = '1';
    qtyInput.placeholder = '0';
    qtyInput.className = 'crm-accessory-item-qty';
    if (item.qty != null) qtyInput.value = item.qty;
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn ghost crm-accessory-item-remove';
    removeBtn.textContent = '✕';
    removeBtn.addEventListener('click', () => row.remove());
    row.appendChild(nameInput);
    row.appendChild(qtyInput);
    row.appendChild(removeBtn);
    crmEquipAccessoriesList.appendChild(row);
  }

  // Fiber installs don't use the wireless SIM/CPE gear — hide those fields. The
  // router field stays (a fibre install may still ship a separate router).
  function applyEquipmentFormForTech(detail) {
    const isFiber = (detail?.technology_type || '').toString().toLowerCase() === 'fiber';
    const setShown = (el, shown) => { if (el) el.style.display = shown ? '' : 'none'; };
    setShown(document.getElementById('crmEquipIccidField'), !isFiber);
    setShown(document.getElementById('crmEquipSimField'), !isFiber);
    setShown(document.getElementById('crmEquipCpeField'), !isFiber);
    const routerLabel = document.getElementById('crmEquipRouterLabel');
    if (routerLabel) routerLabel.textContent = isFiber ? 'Router (from inventory, if supplied)' : 'Router (from inventory)';
    return isFiber;
  }

  // Populate the equipment pickers from the inventory catalogue. Every install
  // element must be a catalogued SKU — no free text (see the submit guard). Only
  // customer-facing and consumable items are surfaced (never infra/tools);
  // router/CPE offer the customer devices, accessories offer both. Items are
  // listed even at **zero** available stock, so picking an out-of-stock SKU
  // still raises the request and prompts Stores to reorder. Best-effort and
  // cached — silently no-op when Inventory is disabled or unreachable.
  let _crmEquipSkuLoaded = false;
  let _crmEquipItems = [];        // [{sku, name, classification, available}]
  let _crmEquipBySku = new Map(); // upper(sku) -> {sku, name}
  let _crmEquipByName = new Map();// lower(name) -> {sku, name}
  async function populateEquipmentSkuLists() {
    if (_crmEquipSkuLoaded) return;
    const routerList = document.getElementById('crmEquipRouterList');
    const ontList = document.getElementById('crmEquipOntList');
    const cpeList = document.getElementById('crmEquipCpeList');
    const accList = document.getElementById('crmEquipAccessoryList');
    if (!routerList && !ontList && !cpeList && !accList) return;
    try {
      const [cust, cons] = await Promise.all([
        adminAPI.fetch(API.inventoryItems('classification=customer'), { method: 'GET' }),
        adminAPI.fetch(API.inventoryItems('classification=consumable'), { method: 'GET' }),
      ]);
      const customer = cust.items || [];
      const consumable = cons.items || [];
      _crmEquipItems = [...customer, ...consumable];
      _crmEquipBySku = new Map();
      _crmEquipByName = new Map();
      for (const i of _crmEquipItems) {
        _crmEquipBySku.set(String(i.sku).toUpperCase(), { sku: i.sku, name: i.name });
        _crmEquipByName.set(String(i.name).toLowerCase(), { sku: i.sku, name: i.name });
      }
      const optsFor = (items) => items
        .map(i => {
          const avail = Number(i.available ?? i.on_hand ?? 0);
          const stock = avail > 0 ? `${avail} in stock` : 'out of stock — will prompt reorder';
          return `<option value="${escapeHtml(i.sku)}">${escapeHtml(i.name)} — ${escapeHtml(stock)}</option>`;
        })
        .join('');
      // Devices go in router/ONT/CPE; every eligible item is offered for accessories.
      if (routerList) routerList.innerHTML = optsFor(customer);
      if (ontList) ontList.innerHTML = optsFor(customer);
      if (cpeList) cpeList.innerHTML = optsFor(customer);
      if (accList) accList.innerHTML = optsFor(_crmEquipItems);
      _crmEquipSkuLoaded = true;
    } catch (_) { /* inventory off / unreachable — the submit guard blocks save */ }
  }

  // Resolve a picker value (a picked SKU, or the item name typed to match a
  // catalogue entry) to a catalogued item, or null when it isn't in inventory.
  function resolveEquipItem(value) {
    const v = (value || '').trim();
    if (!v) return null;
    return _crmEquipBySku.get(v.toUpperCase()) || _crmEquipByName.get(v.toLowerCase()) || null;
  }

  function resetCrmAccessoriesForm(detail = null) {
    if (crmInstallEquipmentForm) crmInstallEquipmentForm.reset();
    populateEquipmentSkuLists();
    const equipment = detail?.equipment && detail.equipment[0] ? detail.equipment[0] : null;
    const isFiber = applyEquipmentFormForTech(detail);
    if (crmEquipIccidTail) crmEquipIccidTail.value = resolveIccidTail(equipment);
    if (crmEquipSim) crmEquipSim.value = equipment?.sim_card_number || '';
    const meta = detail?.meta || detail?.metadata || {};
    const opts = meta.installation_options || {};
    const isRouterOnt = isFiber && (opts.ont_type || '').toString().toLowerCase() === 'router';
    // ONT device (fiber only). For a Router-ONT this is the routing device, so it's
    // the primary field; assign it here rather than under Router.
    const ontField = document.getElementById('crmEquipOntField');
    if (ontField) ontField.style.display = isFiber ? '' : 'none';
    if (crmEquipOnt) crmEquipOnt.value = isFiber ? (equipment?.ont_model || '') : '';
    if (crmEquipRouter) {
      const supplied = installationSuppliesRouter(detail);
      crmEquipRouter.disabled = !supplied;
      if (supplied) {
        crmEquipRouter.value = equipment?.router_model || '';
        crmEquipRouter.title = '';
      } else if (isRouterOnt) {
        // The Router-ONT IS the router — it's assigned in the ONT field above, not here.
        crmEquipRouter.value = 'Provided by the Router-ONT';
        crmEquipRouter.title = 'A Router-ONT already provides routing. Assign the device in the ONT field above.';
      } else {
        crmEquipRouter.value = CUSTOMER_OWNS_ROUTER;
        crmEquipRouter.title = 'The customer provides their own router, so none is issued from stock.';
      }
    }
    if (crmEquipCpe) crmEquipCpe.value = equipment?.cpe_model || '';
    if (!crmEquipAccessoriesList) return;
    crmEquipAccessoriesList.innerHTML = '';
    const existing = equipment && Array.isArray(equipment.accessories)
      ? equipment.accessories
      : null;
    if (existing && existing.length) {
      existing.forEach((item) => addCrmAccessoryRow(item));
      return;
    }
    // No presumptive free-text defaults any more: accessories must be picked from
    // inventory. Start with one empty picker row; anything the operator doesn't
    // add falls back to the technology's category lines on the stock request.
    addCrmAccessoryRow();
  }

  function collectCrmAccessories() {
    if (!crmEquipAccessoriesList) return { items: null, error: null };
    const items = [];
    const rows = crmEquipAccessoriesList.querySelectorAll('.crm-accessory-item-row');
    for (const row of rows) {
      const name = row.querySelector('.crm-accessory-item-name')?.value.trim() || '';
      const qtyRaw = row.querySelector('.crm-accessory-item-qty')?.value.trim() || '';
      if (!name && !qtyRaw) continue;
      if (!name) return { items: null, error: 'Accessory name is required.' };
      const qty = Number(qtyRaw);
      if (!qtyRaw || Number.isNaN(qty) || qty <= 0) {
        return { items: null, error: 'Accessory quantity must be greater than 0.' };
      }
      // Inventory-only: every accessory must resolve to a catalogued item.
      const match = resolveEquipItem(name);
      if (!match) {
        return { items: null, error: `“${name}” isn’t in inventory. Pick a catalogued item from the list.` };
      }
      items.push({ name: match.name, qty, sku: match.sku });
    }
    return { items: items.length ? items : null, error: null };
  }

  function computeSimFromIccidTail(tail) {
    const raw = (tail || '').trim().replace(/\D/g, '');
    if (!raw) return { sim: '', error: null };
    if (!/^\d{4}$/.test(raw) || /^0{4}$/.test(raw)) {
      return { sim: '', error: 'Enter the last 4 digits of the ICCID (not all zeros).' };
    }
    const base = `60707000000${raw}`;
    const sim = (BigInt(base) - 11n).toString();
    return { sim, error: null };
  }

  function computeIccidTailFromSim(sim) {
    const raw = (sim || '').toString().replace(/\D/g, '');
    if (!raw) return '';
    try {
      const base = BigInt(raw) + 11n;
      const tail = (base % 10000n).toString().padStart(4, '0');
      return tail;
    } catch {
      return '';
    }
  }

  function resolveIccidTail(equipment) {
    const explicit = equipment?.sim_iccid || equipment?.iccid || equipment?.iccid_number;
    if (explicit != null && String(explicit).trim()) return String(explicit);
    if (equipment?.sim_card_number) {
      const derived = computeIccidTailFromSim(equipment.sim_card_number);
      if (derived) return derived;
    }
    return '';
  }

  function updateCrmSimFromIccidTail() {
    if (!crmEquipIccidTail || !crmEquipSim) return { error: null };
    const { sim, error } = computeSimFromIccidTail(crmEquipIccidTail.value);
    if (!error) {
      crmEquipSim.value = sim;
    } else {
      crmEquipSim.value = '';
    }
    return { error };
  }

  function installationRequiresRouter(detail) {
    // A customer-owned router is never ours to supply, whatever the install type.
    if (!installationSuppliesRouter(detail)) return false;
    const tech = (detail?.technology_type || '').toString().toLowerCase();
    const meta = detail?.meta || detail?.metadata || {};
    const opts = meta.installation_options || {};
    if (tech === 'fiber') {
      // The WiFi6 router is optional for fiber (customer may own one), so a
      // router model is only needed when we actually supply one.
      return !!opts.wifi6_router;
    }
    const installType = (detail?.installation_type || '').toString().toLowerCase();
    if (installType === 'outdoor_small_router' || installType === 'outdoor_large_router') {
      return true;
    }
    return !!opts.wifi6_router || ['ax', 'small', 'large'].includes((opts.router || '').toString().toLowerCase());
  }

  // Whether this installation takes a router from our stock. Mirrors
  // installation_inventory.installation_supplies_router on the backend, which
  // decides whether the stock request gets a router line at all. When it does not,
  // the customer owns the router: the picker is disabled so a router can't be
  // chosen, stored, and then silently left in stock.
  function installationSuppliesRouter(detail) {
    const meta = detail?.meta || detail?.metadata || {};
    const opts = meta.installation_options || {};
    const router = (opts.router || '').toString().toLowerCase();
    const installType = (detail?.installation_type || '').toString().toLowerCase();
    const needs = !!opts.wifi6_router
      || ['ax', 'small', 'large'].includes(router)
      || installType === 'outdoor_small_router' || installType === 'outdoor_large_router';
    return needs && router !== 'customer_owned';
  }

  const CUSTOMER_OWNS_ROUTER = 'Customer owns router';

  function installationRequiresSim(detail) {
    const tech = (detail?.technology_type || '').toString().toLowerCase();
    return tech === '4g' || tech === 'lte';
  }

  function populateCrmInstallTicketSupervisors() {
    if (!crmInstallTicketSupervisor) return;
    const source = getSupervisorAdminUsers();
    const supervisors = Array.isArray(source) ? source.slice() : [];
    supervisors.sort((a, b) => adminUserSortKey(a).localeCompare(adminUserSortKey(b)));
    crmInstallTicketSupervisor.innerHTML = '<option value="">Select supervisor</option>';
    supervisors.forEach((user) => {
      const option = document.createElement('option');
      option.value = String(user.id);
      option.textContent = primaryAdminUserName(user) || user.email || `User ${user.id}`;
      crmInstallTicketSupervisor.appendChild(option);
    });
  }

  function renderCrmAutoProvisionPayments(detail) {
    if (!crmAutoPaymentsList || !crmAutoInstallTotal || !crmAutoSubscriptionTotal || !crmAutoPaymentsNote) return;
    crmAutoPaymentsList.innerHTML = '';
    const payments = Array.isArray(detail?.payments) ? detail.payments : [];
    if (!payments.length) {
      crmAutoPaymentsList.innerHTML = '<div style="color:var(--sub);">No payments recorded.</div>';
      crmAutoInstallTotal.textContent = formatCurrency(0);
      crmAutoSubscriptionTotal.textContent = '—';
      crmAutoPaymentsNote.textContent = '';
      return;
    }
    let installTotal = 0;
    let subscriptionTotal = 0;
    payments.forEach((pay) => {
      const amount = Number(pay.amount || 0);
      if (pay.payment_type === 'initial') installTotal += amount;
      if (pay.payment_type === 'supplementary') subscriptionTotal += amount;
      const row = document.createElement('div');
      row.className = 'crm-auto-payments-row';
      const paidAt = pay.paid_at || pay.created_at || '';
      const date = paidAt ? formatDate(paidAt) : '—';
      row.innerHTML = `
        <div>${escapeHtml(date)}</div>
        <div>${escapeHtml(formatCrmLabel(pay.payment_type || ''))}</div>
        <div>${formatCurrency(amount)}</div>
        <div>${escapeHtml(pay.receipt_ref || '—')}</div>
        <div>${pay.billing_officer_id != null ? `#${escapeHtml(String(pay.billing_officer_id))}` : '—'}</div>
      `;
      crmAutoPaymentsList.appendChild(row);
    });
    crmAutoInstallTotal.textContent = formatCurrency(installTotal);
    if (detail?.customer_type === 'corporate' && subscriptionTotal <= 0) {
      // Postpaid by default — no subscription payment was taken.
      crmAutoSubscriptionTotal.textContent = 'Not applicable (corporate)';
      crmAutoPaymentsNote.textContent = 'Corporate customers are postpaid. Only installation invoice and payment are posted.';
    } else {
      crmAutoSubscriptionTotal.textContent = formatCurrency(subscriptionTotal);
      crmAutoPaymentsNote.textContent = detail?.customer_type === 'corporate'
        ? 'Corporate (postpaid): a subscription payment was recorded for this customer and is posted to their profile.'
        : 'First subscription payment is posted directly to the customer profile (no invoice).';
    }
  }

  function prepareCrmAutoProvisionOverrides(detail) {
    if (!crmAutoEmailWrap || !crmAutoEmail || !crmAutoSimWrap || !crmAutoSim || !crmAutoDidWrap || !crmAutoDid || !crmAutoOverridesNote) {
      return;
    }
    const tech = (detail?.technology_type || '').toString().toLowerCase();
    const isFiberEmail = tech === 'fiber' || tech === 'wimax' || tech === 'ptp';
    const is4g = tech === '4g' || tech === 'lte';
    crmAutoEmailWrap.style.display = isFiberEmail ? 'block' : 'none';
    crmAutoSimWrap.style.display = is4g ? 'block' : 'none';
    crmAutoDidWrap.style.display = is4g ? 'block' : 'none';
    if (crmAutoDidFetch) crmAutoDidFetch.style.display = is4g ? 'inline-flex' : 'none';
    crmAutoEmail.required = isFiberEmail;
    crmAutoEmail.value = detail?.contact_email || '';
    const simValue = detail?.equipment && detail.equipment[0] ? detail.equipment[0].sim_card_number : '';
    crmAutoSim.value = simValue || '';
    const meta = detail?.meta || detail?.metadata || {};
    const didId = meta.porta_did_number || meta.porta_did_number_override || '';
    const didMsisdn = meta.porta_did_msisdn || '';
    crmAutoDid.value = didMsisdn || didId || '';
    if (isFiberEmail) {
      crmAutoOverridesNote.textContent = 'Email is required for WiMAX, Fiber, and PTP auto-provisioning.';
    } else if (is4g) {
      crmAutoOverridesNote.textContent = 'Update SIM or DID number before provisioning if needed.';
      if (!didId) {
        fetchCrmAutoDidNumber();
      }
    } else {
      crmAutoOverridesNote.textContent = 'No additional provisioning details required for this technology.';
    }
  }

  async function fetchCrmAutoDidNumber() {
    if (!crmAutoDid) {
      // eslint-disable-next-line no-console
      console.warn('crm-installations: #crmAutoDid missing.');
      return;
    }
    const id = crmInstallationsState.selectedId;
    if (!id) return;
    try {
      crmAutoDidFetch && crmAutoDidFetch.setAttribute('disabled', 'disabled');
      const data = await adminAPI.fetch(API.crmInstallationDidNumber(id), { method: 'POST' });
      const didValue = data?.did_number || '';
      crmAutoDid.value = data?.msisdn || didValue;
      const detail = crmInstallationsState.selectedDetail;
      if (detail) {
        detail.meta = detail.meta || {};
        detail.meta.porta_did_number = didValue;
        if (data?.msisdn) detail.meta.porta_did_msisdn = data.msisdn;
        if (data?.description) detail.meta.porta_did_description = data.description;
      }
      if (data?.msisdn) {
        crmAutoOverridesNote.textContent = `Reserved DID: ${data.msisdn} (ID ${didValue}).`;
      }
    } catch (err) {
      showFlash(err?.message || 'Failed to reserve DID number', 'error');
    } finally {
      crmAutoDidFetch && crmAutoDidFetch.removeAttribute('disabled');
    }
  }

  // Product/component edits are allowed only pre-provisioning and non-terminal
  // (mirrors the backend guard in change_installation_product).
  const _PRODUCT_EDITABLE_STATUSES = new Set([
    'customer_paid', 'customer_validated', 'pole_delivered', 'equipment_assigned',
  ]);
  function _installProductEditable(detail) {
    if (!detail) return false;
    if (detail.porta_created_at) return false;
    return _PRODUCT_EDITABLE_STATUSES.has(String(detail.status || ''));
  }

  const _DETAILS_TECH_TYPES = {
    fiber: ['fiber'],
    wimax: ['outdoor_only', 'outdoor_pole_bracket', 'outdoor_small_router', 'outdoor_large_router'],
    '4g': ['outdoor_only', 'outdoor_pole_bracket', 'outdoor_small_router', 'outdoor_large_router'],
  };
  const _TYPE_LABELS = {
    fiber: 'Fiber (drop cable + ONT)',
    outdoor_only: 'Outdoor only',
    outdoor_small_router: 'Outdoor + small router',
    outdoor_large_router: 'Outdoor + large router',
    outdoor_pole_bracket: 'Outdoor + pole/bracket',
  };
  function _filterDetailsTypeOptions(tech, current) {
    if (!crmInstallDetailsType) return;
    const allowed = _DETAILS_TECH_TYPES[tech] || Object.keys(_TYPE_LABELS);
    const keep = allowed.includes(current) ? current : allowed[0];
    crmInstallDetailsType.innerHTML = allowed
      .map((v) => `<option value="${v}">${escapeHtml(_TYPE_LABELS[v] || v)}</option>`).join('');
    crmInstallDetailsType.value = keep;
  }

  // Editable component picker (Details dialog) — mirrors the create form's
  // technology-aware options and collects into the same `installation_options` shape.
  function _renderDetailsComponents(tech, opts) {
    const box = crmInstallDetailsComponents;
    if (!box) return;
    opts = opts || {};
    const isFiber = tech === 'fiber';
    const isWireless = tech === '4g' || tech === 'wimax';
    const chk = (key, label, { show = true, forceOn = false } = {}) => {
      if (!show) return '';
      const checked = forceOn || !!opts[key];
      return `<label style="display:inline-flex; align-items:center; gap:6px; font-size:12px;">
        <input type="checkbox" data-comp="${key}" ${checked ? 'checked' : ''} ${forceOn ? 'disabled data-forceon="1"' : ''} /> ${escapeHtml(label)}</label>`;
    };
    const routerVal = (opts.router || '').toString();
    const routerRadios = isWireless ? `
      <div style="grid-column:1 / -1; font-size:12px;"><span class="muted">Router:</span>
        ${['ax', 'small', 'customer_owned'].map((v) => `
          <label style="display:inline-flex; align-items:center; gap:4px; margin-left:10px;">
            <input type="radio" name="crmDetailsRouter" data-comp-router="${v}" ${routerVal === v ? 'checked' : ''} /> ${v === 'customer_owned' ? 'Customer owns' : v.toUpperCase()}</label>`).join('')}
      </div>` : '';
    const ontType = (opts.ont_type || '').toString();
    const ontRadios = isFiber ? `
      <div style="grid-column:1 / -1; font-size:12px;"><span class="muted">ONT type:</span>
        ${['router', 'modem'].map((v) => `
          <label style="display:inline-flex; align-items:center; gap:4px; margin-left:10px;">
            <input type="radio" name="crmDetailsOntType" data-comp-onttype="${v}" ${ontType === v ? 'checked' : ''} /> ${v === 'router' ? 'Router-ONT' : 'Modem-ONT'}</label>`).join('')}
      </div>` : '';
    box.innerHTML = `
      <div style="display:grid; grid-template-columns: repeat(3, minmax(150px, 1fr)); gap:6px 12px; align-items:center;">
        ${chk('cpe', 'CPE', { show: isWireless, forceOn: isWireless })}
        ${chk('wifi6_router', 'WiFi6 router', { show: isFiber })}
        ${chk('drop_cable', 'Drop cable', { show: true, forceOn: isFiber })}
        ${chk('cables', 'Cables (RJ45)', { show: true, forceOn: isWireless })}
        ${chk('clips_bracket', 'Clips / bracket')}
        ${chk('pole', 'Pole')}
        ${chk('ont', 'ONT', { show: isFiber, forceOn: isFiber })}
        ${routerRadios}
        ${ontRadios}
      </div>`;
    // WiFi6 router is only relevant with a modem ONT.
    const syncWifi6 = () => {
      const w = box.querySelector('[data-comp="wifi6_router"]');
      if (!w) return;
      const modem = box.querySelector('[data-comp-onttype="modem"]');
      const isModem = !!(modem && modem.checked);
      if (!isModem) { w.checked = false; w.disabled = true; } else { w.disabled = false; }
    };
    if (isFiber) {
      box.querySelectorAll('[data-comp-onttype]').forEach((r) => r.addEventListener('change', syncWifi6));
      syncWifi6();
    }
  }

  function _collectDetailsComponents() {
    const box = crmInstallDetailsComponents;
    if (!box) return null;
    const opts = {};
    box.querySelectorAll('[data-comp]').forEach((el) => { opts[el.getAttribute('data-comp')] = !!el.checked; });
    const router = box.querySelector('[data-comp-router]:checked');
    opts.router = router ? router.getAttribute('data-comp-router') : null;
    const ontType = box.querySelector('[data-comp-onttype]:checked');
    opts.ont_type = ontType ? ontType.getAttribute('data-comp-onttype') : null;
    opts.technology = crmInstallDetailsTechnology ? crmInstallDetailsTechnology.value : null;
    return opts;
  }

  function _syncDetailsTechWarning() {
    if (!crmInstallDetailsTechWarn || !crmInstallDetailsForm || !crmInstallDetailsTechnology) return;
    const orig = crmInstallDetailsForm.dataset.origTech || '';
    const changed = orig && crmInstallDetailsTechnology.value !== orig;
    crmInstallDetailsTechWarn.style.display = changed ? '' : 'none';
    crmInstallDetailsTechWarn.textContent = changed
      ? '⚠ Changing technology resets the assigned equipment (router/CPE/SIM/accessories) — you’ll re-run “Assign equipment” for the new technology.'
      : '';
  }

  function renderCrmInstallDetails(detail) {
    if (!crmInstallDetailsMaterials || !crmInstallDetailsPayments) return;
    crmInstallDetailsMaterials.innerHTML = '';
    crmInstallDetailsPayments.innerHTML = '';
    const canEdit = hasRole('root', 'inventory_supervisor');
    const productEditable = canEdit && _installProductEditable(detail);
    if (crmInstallDetailsSave) {
      crmInstallDetailsSave.style.display = canEdit ? '' : 'none';
    }
    if (crmInstallDetailsForm) {
      crmInstallDetailsForm.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !canEdit;
      });
    }
    // Technology/plan/components lock once provisioned or terminal; contact/address
    // stay editable for the editable roles.
    if (canEdit && !productEditable) {
      [crmInstallDetailsCustomerType, crmInstallDetailsTechnology, crmInstallDetailsType,
        crmInstallDetailsPackage, crmInstallDetailsAccessPoints].forEach((el) => { if (el) el.disabled = true; });
    }
    if (!detail) {
      crmInstallDetailsMaterials.innerHTML = '<div style="color:var(--sub);">No details available.</div>';
      crmInstallDetailsPayments.innerHTML = '<div style="color:var(--sub);">No additional payments.</div>';
      return;
    }
    if (crmInstallDetailsCustomerType) {
      crmInstallDetailsCustomerType.value = detail.customer_type || 'home';
    }
    if (crmInstallDetailsTechnology) {
      crmInstallDetailsTechnology.value = detail.technology_type || 'wimax';
    }
    if (crmInstallDetailsType) {
      crmInstallDetailsType.value = detail.installation_type || 'outdoor_only';
    }
    if (crmInstallDetailsExpected) {
      crmInstallDetailsExpected.value = detail.expected_install_date
        ? String(detail.expected_install_date).split('T')[0]
        : '';
    }
    if (crmInstallDetailsAccessPoints) {
      crmInstallDetailsAccessPoints.value = detail.access_point_count != null
        ? String(detail.access_point_count)
        : '';
    }
    if (crmInstallDetailsContactName) crmInstallDetailsContactName.value = detail.contact_name || '';
    if (crmInstallDetailsContactEmail) crmInstallDetailsContactEmail.value = detail.contact_email || '';
    const phoneNumbers = Array.isArray(detail.phone_numbers) ? detail.phone_numbers.filter(Boolean) : [];
    const primaryPhone = detail.contact_phone || phoneNumbers[0] || '';
    const secondaryPhone = phoneNumbers.find((num) => String(num) !== String(primaryPhone)) || '';
    const primaryParts = splitPhoneWithCode(primaryPhone);
    const secondaryParts = splitPhoneWithCode(secondaryPhone);
    if (crmInstallDetailsContactPhoneCode) crmInstallDetailsContactPhoneCode.value = primaryParts.code || '220';
    if (crmInstallDetailsContactPhoneNumber) crmInstallDetailsContactPhoneNumber.value = primaryParts.number || '';
    if (crmInstallDetailsSecondaryPhoneCode) crmInstallDetailsSecondaryPhoneCode.value = secondaryParts.code || '220';
    if (crmInstallDetailsSecondaryPhoneNumber) crmInstallDetailsSecondaryPhoneNumber.value = secondaryParts.number || '';
    const addressParts = extractAddressParts(detail);
    if (crmInstallDetailsStreet) crmInstallDetailsStreet.value = addressParts.street || '';
    if (crmInstallDetailsCity) crmInstallDetailsCity.value = addressParts.city || '';
    if (crmInstallDetailsRegion) crmInstallDetailsRegion.value = addressParts.region || '';
    if (crmInstallDetailsNotes) crmInstallDetailsNotes.value = detail.notes || '';
    loadCrmPlansForSelect(
      crmInstallDetailsPackage,
      detail.customer_type || '',
      detail.technology_type || ''
    ).then(() => {
      if (crmInstallDetailsPackage) {
        crmInstallDetailsPackage.value = detail.package_code || '';
      }
    });
    const meta = detail.meta || detail.metadata || {};
    const opts = meta.installation_options || {};
    const detailTech = detail.technology_type || 'wimax';
    _filterDetailsTypeOptions(detailTech, detail.installation_type || '');
    if (crmInstallDetailsForm) crmInstallDetailsForm.dataset.origTech = detail.technology_type || '';
    if (crmInstallDetailsTechWarn) { crmInstallDetailsTechWarn.style.display = 'none'; crmInstallDetailsTechWarn.textContent = ''; }

    if (productEditable) {
      // Editable component picker (re-renders on Technology change).
      if (crmInstallDetailsComponents) crmInstallDetailsComponents.style.display = '';
      if (crmInstallDetailsMaterials) crmInstallDetailsMaterials.style.display = 'none';
      _renderDetailsComponents(detailTech, opts);
    } else {
      // Read-only materials list (locked window / view-only role).
      if (crmInstallDetailsComponents) crmInstallDetailsComponents.style.display = 'none';
      if (crmInstallDetailsMaterials) crmInstallDetailsMaterials.style.display = '';
      const materials = [];
      if (opts.cpe) materials.push('CPE');
      if (opts.drop_cable) materials.push('Drop cable');
      if (opts.cables) materials.push('Cables');
      if (opts.clips_bracket) materials.push('Clips/Bracket');
      if (opts.pole) materials.push('Pole');
      if (opts.ont) materials.push('ONT');
      if (opts.wifi6_router) materials.push('WiFi6 router');
      if (opts.router === 'ax') materials.push('AX router');
      if (opts.router === 'small') materials.push('Small router');
      if (opts.router === 'customer_owned') materials.push('Customer owned router');

      if (materials.length) {
        const list = document.createElement('ul');
        list.className = 'crm-install-details-items';
        materials.forEach((item) => {
          const li = document.createElement('li');
          li.textContent = item;
          list.appendChild(li);
        });
        crmInstallDetailsMaterials.appendChild(list);
      } else {
        crmInstallDetailsMaterials.innerHTML = '<div style="color:var(--sub);">No materials listed.</div>';
      }
    }

    const payments = Array.isArray(detail.payments) ? detail.payments : [];
    const extraPayments = payments.filter((pay) => pay.payment_type === 'supplementary');
    if (!extraPayments.length) {
      crmInstallDetailsPayments.innerHTML = '<div style="color:var(--sub);">No additional payments.</div>';
      return;
    }
    extraPayments.forEach((pay) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'crm-install-details-payment';
      const paidAt = pay.paid_at || pay.created_at || '';
      const date = paidAt ? formatDate(paidAt) : '—';
      const items = Array.isArray(pay.items) ? pay.items : [];
      const isFirstMonth = items.some((item) => {
        const name = item && item.name ? String(item.name).toLowerCase() : '';
        return name.includes('first month subscription');
      });
      const header = document.createElement('div');
      header.className = 'crm-install-details-payment-header';
      header.textContent = `${formatCurrency(pay.amount)} · ${date} · ${pay.receipt_ref || 'No receipt'}${isFirstMonth ? ' · First month subscription' : ''}`;
      wrapper.appendChild(header);
      if (items.length) {
        const list = document.createElement('ul');
        list.className = 'crm-install-details-items';
        items.forEach((item) => {
          const name = item && item.name ? String(item.name) : 'Item';
          const qty = item && item.qty != null ? Number(item.qty) : null;
          const li = document.createElement('li');
          li.textContent = qty != null ? `${name} × ${qty}` : name;
          list.appendChild(li);
        });
        wrapper.appendChild(list);
      }
      crmInstallDetailsPayments.appendChild(wrapper);
    });
  }

  function renderCrmValidateMaterials(detail) {
    if (!crmValidateMaterialsList) return;
    crmValidateMaterialsList.innerHTML = '';
    if (crmValidatePaymentsList) {
      crmValidatePaymentsList.innerHTML = '';
    }
    if (!detail) {
      crmValidateMaterialsList.innerHTML = '<div style="color:var(--sub);">No materials listed.</div>';
      if (crmValidatePaymentsList) {
        crmValidatePaymentsList.innerHTML = '<div style="color:var(--sub);">No payments recorded.</div>';
      }
      return;
    }
    const meta = detail.meta || detail.metadata || {};
    const opts = meta.installation_options || {};
    const materials = [];
    if (opts.cpe) materials.push('CPE');
    if (opts.drop_cable) materials.push('Drop cable');
    if (opts.cables) materials.push('Cables');
    if (opts.clips_bracket) materials.push('Clips/Bracket');
    if (opts.pole) materials.push('Pole');
    if (opts.ont) materials.push('ONT');
    if (opts.wifi6_router) materials.push('WiFi6 router');
    if (opts.router === 'ax') materials.push('AX router');
    if (opts.router === 'small') materials.push('Small router');
    if (opts.router === 'customer_owned') materials.push('Customer owned router');

    const extraItems = new Set();
    const payments = Array.isArray(detail.payments) ? detail.payments : [];
    payments.forEach((pay) => {
      if (pay.payment_type !== 'supplementary') return;
      const items = Array.isArray(pay.items) ? pay.items : [];
      items.forEach((item) => {
        const name = item && item.name ? String(item.name).trim() : '';
        if (name) extraItems.add(name);
      });
    });
    materials.push(...Array.from(extraItems));

    if (materials.length) {
      const list = document.createElement('ul');
      list.className = 'crm-install-details-items';
      const header = document.createElement('div');
      header.style.color = 'var(--sub)';
      header.style.fontSize = '12px';
      header.style.marginBottom = '6px';
      header.textContent = `${materials.length} item${materials.length === 1 ? '' : 's'}`;
      crmValidateMaterialsList.appendChild(header);
      materials.forEach((item) => {
        const li = document.createElement('li');
        li.textContent = item;
        list.appendChild(li);
      });
      crmValidateMaterialsList.appendChild(list);
    } else {
      crmValidateMaterialsList.innerHTML = '<div style="color:var(--sub);">No materials listed.</div>';
    }

    if (crmValidatePaymentsList) {
      if (!payments.length) {
        crmValidatePaymentsList.innerHTML = '<div style="color:var(--sub);">No payments recorded.</div>';
      } else {
        payments.forEach((pay) => {
          const paidAt = pay.paid_at || pay.created_at || '';
          const date = paidAt ? formatDate(paidAt) : '—';
          const row = document.createElement('div');
          row.className = 'crm-install-details-payment';
          row.innerHTML = `
            <div class="crm-install-details-payment-header">
              ${formatCurrency(pay.amount)} · ${date} · ${escapeHtml(pay.receipt_ref || 'No receipt')}
            </div>
          `;
          crmValidatePaymentsList.appendChild(row);
        });
      }
    }
  }

  function filterPlansByTechnology(items, tech) {
    if (!tech) return items;
    const needle = String(tech).toLowerCase();
    return items.filter((item) => {
      const label = String(item.label || '').toLowerCase();
      const code = String(item.porta_product_code || '').toLowerCase();
      if (needle === 'wimax') {
        return label.includes('wimax') || code.includes('wimax') || label.includes('vpn');
      }
      if (needle === '4g') {
        return (
          label.includes('4g')
          || label.includes('lte')
          || code.includes('4g')
          || code.includes('lte')
          || label.includes('na korda')
          || label.includes('ultra saver')
          || label.includes('super saver')
          || label.includes('jula')
          || label.includes('mbotai')
        );
      }
      if (needle === 'fiber') {
        return label.includes('fiber') || code.includes('fiber') || label.includes('dedicated');
      }
      return true;
    });
  }

  function renderCrmPlanOptions(items, customerType, tech) {
    if (!crmInstallPackage) return;
    const filtered = filterPlansByTechnology(items, tech);
    const list = tech ? filtered : items;
    crmInstallPackage.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = list.length
      ? 'Select plan'
      : (items.length ? `No plans for ${tech || 'selected technology'}` : 'No plans synced');
    crmInstallPackage.appendChild(placeholder);
    list.forEach((item) => {
      const option = document.createElement('option');
      option.value = item.porta_product_code;
      option.textContent = item.label
        ? `${item.label} (${item.porta_product_code})`
        : item.porta_product_code;
      crmInstallPackage.appendChild(option);
    });
    crmInstallPackage.dataset.customerType = customerType || '';
  }

  async function loadCrmPlansForCustomerType(customerType) {
    if (!crmInstallPackage) { console.warn('crm-installations: #crmInstallPackage missing.'); return; }
    const key = customerType || 'all';
    if (crmPlanCache.has(key)) {
      renderCrmPlanOptions(crmPlanCache.get(key), customerType, crmInstallTechnology ? crmInstallTechnology.value : '');
      return;
    }
    crmInstallPackage.innerHTML = '<option value="">Loading plans…</option>';
    try {
      await ensureCrmPlanCache(customerType);
      const items = crmPlanCache.get(key) || [];
      renderCrmPlanOptions(items, customerType, crmInstallTechnology ? crmInstallTechnology.value : '');
    } catch (err) {
      crmInstallPackage.innerHTML = '<option value="">Failed to load plans</option>';
      showFlash(err?.message || 'Failed to load plans', 'error');
    }
  }

  function renderCrmAlerts(alerts = []) {
    if (!crmAlertOverlay || !crmAlertStack) return;
    crmAlertStack.innerHTML = '';
    if (!alerts.length) {
      crmAlertOverlay.hidden = true;
      return;
    }
    const canClear = hasRole('root', 'system_admin');
    alerts.forEach((alert) => {
      const card = document.createElement('div');
      card.className = `crm-alert-card ${alert.level === 'warn' ? 'warn' : ''}`;
      const content = document.createElement('div');
      const title = document.createElement('div');
      title.className = 'crm-alert-title';
      title.textContent = alert.title || 'CRM alert';
      const body = document.createElement('div');
      body.className = 'crm-alert-body';
      body.textContent = alert.message || '';
      content.appendChild(title);
      content.appendChild(body);
      const actions = document.createElement('div');
      actions.className = 'crm-alert-actions';
      const dismiss = document.createElement('button');
      dismiss.className = 'crm-alert-dismiss';
      dismiss.type = 'button';
      dismiss.textContent = '✕';
      dismiss.disabled = !canClear;
      dismiss.title = canClear ? 'Dismiss' : 'Only root/system admin can dismiss';
      dismiss.addEventListener('click', () => {
        if (!canClear) return;
        card.remove();
        if (!crmAlertStack.children.length) {
          crmAlertOverlay.hidden = true;
        }
      });
      actions.appendChild(dismiss);
      card.appendChild(content);
      card.appendChild(actions);
      crmAlertStack.appendChild(card);
    });
    crmAlertOverlay.hidden = false;
  }

  function formatInstallationStatus(status) {
    const label = formatCrmLabel(status);
    return `<span class="crm-install-status-badge ${crmStatusClass(status)}">${escapeHtml(label)}</span>`;
  }

  function deriveInstallationAge(paidAt) {
    if (!paidAt) return null;
    const paid = new Date(paidAt);
    if (Number.isNaN(paid.getTime())) return null;
    const now = new Date();
    const diff = Math.max(0, Math.floor((now - paid) / (24 * 3600 * 1000)));
    return diff;
  }

  function updateInstallOptionsForTech() {
    if (!crmInstallTechnology || !crmInstallOptCpe) return;
    const tech = crmInstallTechnology.value;
    const isFiber = tech === 'fiber';
    const isWireless = tech === '4g' || tech === 'wimax';
    document.querySelectorAll('.crm-install-option[data-tech]').forEach((node) => {
      const tag = node.dataset.tech;
      let visible = true;
      if (tag === 'all') {
        visible = true;
      } else if (tag === 'fiber') {
        visible = isFiber;
      } else if (tag === 'wireless') {
        visible = isWireless;
      }
      node.hidden = !visible;
      node.style.display = visible ? '' : 'none';
    });
    if (crmInstallRouterGroup) {
      crmInstallRouterGroup.hidden = !isWireless;
      crmInstallRouterGroup.style.display = isWireless ? '' : 'none';
    }

    if (crmInstallOptCpe) {
      if (isWireless) {
        crmInstallOptCpe.checked = true;
        crmInstallOptCpe.disabled = true;
      } else {
        crmInstallOptCpe.checked = false;
        crmInstallOptCpe.disabled = false;
      }
    }
    if (crmInstallOptDropCable) {
      if (isFiber) {
        crmInstallOptDropCable.checked = true;
        crmInstallOptDropCable.disabled = true;
      } else {
        crmInstallOptDropCable.checked = false;
        crmInstallOptDropCable.disabled = false;
      }
    }

    if (crmInstallOptCables) {
      crmInstallOptCables.disabled = false;
      if (isWireless) {
        crmInstallOptCables.checked = true;
        crmInstallOptCables.disabled = true;
      }
    }
    if (crmInstallOptOnt) {
      crmInstallOptOnt.checked = isFiber;
      crmInstallOptOnt.disabled = isFiber;
    }
    if (isFiber) {
      // Default to Router-ONT (no separate WiFi6 router needed) when nothing
      // has been chosen yet.
      if (crmInstallOntTypeRouter && crmInstallOntTypeModem
          && !crmInstallOntTypeRouter.checked && !crmInstallOntTypeModem.checked) {
        crmInstallOntTypeRouter.checked = true;
      }
    } else {
      if (crmInstallOntTypeRouter) crmInstallOntTypeRouter.checked = false;
      if (crmInstallOntTypeModem) crmInstallOntTypeModem.checked = false;
    }
    updateFiberOntDependents();
    if (isFiber) {
      if (crmInstallRouterAx) crmInstallRouterAx.checked = false;
      if (crmInstallRouterSmall) crmInstallRouterSmall.checked = false;
      if (crmInstallRouterOwned) crmInstallRouterOwned.checked = false;
    }
  }

  // A WiFi6 router is only relevant with a modem ONT (a router-ONT already
  // provides routing), and even then it is OPTIONAL — the customer may already
  // own one. So the checkbox is selectable only for a modem ONT and is never
  // force-checked.
  function updateFiberOntDependents() {
    if (!crmInstallOptWifi6) return;
    const isFiber = crmInstallTechnology && crmInstallTechnology.value === 'fiber';
    const modem = isFiber && crmInstallOntTypeModem && crmInstallOntTypeModem.checked;
    if (modem) {
      crmInstallOptWifi6.disabled = false;
    } else {
      crmInstallOptWifi6.checked = false;
      crmInstallOptWifi6.disabled = true;
    }
  }

  function deriveInstallationTypeFromOptions() {
    const tech = crmInstallTechnology ? crmInstallTechnology.value : '';
    if (tech === 'fiber') return 'fiber';
    const poleSelected = crmInstallOptPole && crmInstallOptPole.checked;
    const clipsSelected = crmInstallOptClips && crmInstallOptClips.checked;
    if (poleSelected || clipsSelected) return 'outdoor_pole_bracket';
    if (crmInstallRouterOwned && crmInstallRouterOwned.checked) return 'outdoor_only';
    if (crmInstallRouterSmall && crmInstallRouterSmall.checked) return 'outdoor_small_router';
    if (crmInstallRouterAx && crmInstallRouterAx.checked) return 'outdoor_large_router';
    return 'outdoor_only';
  }

  function collectInstallationOptions() {
    const router = crmInstallRouterAx && crmInstallRouterAx.checked
      ? 'ax'
      : (crmInstallRouterSmall && crmInstallRouterSmall.checked
        ? 'small'
        : (crmInstallRouterOwned && crmInstallRouterOwned.checked ? 'customer_owned' : null));
    const ontType = (crmInstallOntTypeModem && crmInstallOntTypeModem.checked)
      ? 'modem'
      : ((crmInstallOntTypeRouter && crmInstallOntTypeRouter.checked) ? 'router' : null);
    return {
      cpe: !!(crmInstallOptCpe && crmInstallOptCpe.checked),
      drop_cable: !!(crmInstallOptDropCable && crmInstallOptDropCable.checked),
      cables: !!(crmInstallOptCables && crmInstallOptCables.checked),
      clips_bracket: !!(crmInstallOptClips && crmInstallOptClips.checked),
      pole: !!(crmInstallOptPole && crmInstallOptPole.checked),
      ont: !!(crmInstallOptOnt && crmInstallOptOnt.checked),
      ont_type: ontType,
      // Optional WiFi6 router we supply — only applicable to a modem ONT.
      wifi6_router: ontType === 'modem' && !!(crmInstallOptWifi6 && crmInstallOptWifi6.checked),
      router,
      technology: crmInstallTechnology ? crmInstallTechnology.value : null,
    };
  }

  function updateSubscriptionFieldVisibility(customerType) {
    if (!crmInstallSubscriptionField) return;
    // The first-subscription payment is shown for all customer types: required
    // for Home/SME (prepaid) and OPTIONAL for corporate (postpaid) — corporate
    // may still choose to pay one. The required/optional rule is enforced on submit.
    crmInstallSubscriptionField.hidden = false;
    if (crmInstallSubscriptionReceiptField) {
      crmInstallSubscriptionReceiptField.hidden = false;
    }
  }

  function renderBulkPoleForms(items) {
    if (!crmPoleBulkList) return;
    crmPoleBulkList.innerHTML = '';
    if (!items.length) {
      crmPoleBulkList.innerHTML = '<div style="color:var(--sub);">No installations selected.</div>';
      return;
    }
    items.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'crm-pole-bulk-item';
      row.dataset.installationId = item.id;
      row.innerHTML = `
        <div class="crm-pole-bulk-header">
          <strong>${escapeHtml(item.contact_name || item.address || item.id)}</strong>
          <div class="crm-pole-bulk-sub">${escapeHtml(item.contact_phone || '')}${item.address ? ` · ${escapeHtml(item.address)}` : ''}</div>
        </div>
        <div class="crm-pole-bulk-grid">
          <label>Sheet number <input type="text" data-field="sheet" /></label>
          <label>Pole size
            <select data-field="size">
              <option value="none">None</option>
              <option value="2m">2m</option>
              <option value="4m">4m</option>
              <option value="6m">6m</option>
            </select>
          </label>
          <label>Mounting material
            <select data-field="mounting">
              <option value="full_bracket">Full bracket</option>
              <option value="bracket_only">Male bracket only</option>
              <option value="clip_only">Clip only</option>
            </select>
          </label>
          <label>Driver name <input type="text" data-field="driver" /></label>
          <label>Delivered at <input type="datetime-local" data-field="delivered" /></label>
        </div>
      `;
      crmPoleBulkList.appendChild(row);
    });
  }

  function normalizePhoneWithCode(codeValue, numberValue, options = {}) {
    const allowEmpty = Boolean(options.allowEmpty);
    const code = String(codeValue || '').replace(/\D/g, '');
    const number = String(numberValue || '').replace(/\D/g, '');
    if (!code && !number) return { value: null, error: null };
    if (!number) {
      return allowEmpty ? { value: null, error: null } : { value: null, error: 'Phone number is required.' };
    }
    if (!code) return { value: null, error: 'Country code is required.' };
    if (code.length < 1 || code.length > 3) {
      return { value: null, error: 'Country code must be 1 to 3 digits.' };
    }
    if (number.length < 7) {
      return { value: null, error: 'Phone number must be at least 7 digits.' };
    }
    return { value: `${code}${number}`, error: null };
  }

  function splitPhoneWithCode(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return { code: '220', number: '' };
    if (digits.startsWith('220') && digits.length > 3) {
      return { code: '220', number: digits.slice(3) };
    }
    if (digits.length <= 7) {
      return { code: '220', number: digits };
    }
    const codeLength = Math.min(3, Math.max(1, digits.length - 7));
    return { code: digits.slice(0, codeLength), number: digits.slice(codeLength) };
  }

  function addCrmPaymentItemRow(item = {}) {
    if (!crmPaymentItemsList) return;
    const row = document.createElement('div');
    row.className = 'crm-payment-item-row';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.placeholder = 'Item';
    nameInput.className = 'crm-payment-item-name';
    if (item.name) nameInput.value = item.name;
    const amountInput = document.createElement('input');
    amountInput.type = 'number';
    amountInput.min = '0';
    amountInput.step = '0.01';
    amountInput.placeholder = '0.00';
    amountInput.className = 'crm-payment-item-amount';
    if (item.amount != null) amountInput.value = item.amount;
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn ghost crm-payment-item-remove';
    removeBtn.textContent = '✕';
    removeBtn.addEventListener('click', () => {
      row.remove();
      updateCrmPaymentItemsTotal();
    });
    amountInput.addEventListener('input', updateCrmPaymentItemsTotal);
    nameInput.addEventListener('input', updateCrmPaymentItemsTotal);
    row.appendChild(nameInput);
    row.appendChild(amountInput);
    row.appendChild(removeBtn);
    crmPaymentItemsList.appendChild(row);
    updateCrmPaymentItemsTotal();
  }

  function resetCrmPaymentForm() {
    if (crmInstallPaymentForm) crmInstallPaymentForm.reset();
    if (crmPaymentItemsList) {
      crmPaymentItemsList.innerHTML = '';
      addCrmPaymentItemRow();
    }
    updateCrmPaymentItemsTotal();
  }

  function collectCrmPaymentItems() {
    if (!crmPaymentItemsList) return { items: null, error: null };
    const items = [];
    const rows = crmPaymentItemsList.querySelectorAll('.crm-payment-item-row');
    for (const row of rows) {
      const name = row.querySelector('.crm-payment-item-name')?.value.trim() || '';
      const amountRaw = row.querySelector('.crm-payment-item-amount')?.value.trim() || '';
      if (!name && !amountRaw) continue;
      if (!name) return { items: null, error: 'Item name is required for each payment item.' };
      const amount = Number(amountRaw);
      if (!amountRaw || Number.isNaN(amount) || amount <= 0) {
        return { items: null, error: 'Item amount must be greater than 0.' };
      }
      items.push({ name, amount });
    }
    return { items: items.length ? items : null, error: null };
  }

  function updateCrmPaymentItemsTotal() {
    if (!crmPaymentItemsList) return;
    const rows = crmPaymentItemsList.querySelectorAll('.crm-payment-item-row');
    let total = 0;
    rows.forEach((row) => {
      const amountRaw = row.querySelector('.crm-payment-item-amount')?.value || '';
      const amount = Number(amountRaw);
      if (!Number.isNaN(amount)) total += amount;
    });
    if (crmPaymentItemsTotal) {
      crmPaymentItemsTotal.textContent = `Total: ${formatCurrency(total)}`;
    }
    if (crmPaymentAmount) {
      crmPaymentAmount.value = total ? String(total) : '';
    }
  }

  function renderInstallationsDashboard(report) {
    if (!crmInstallationsDashboard) return;
    const kpis = report && report.kpis ? report.kpis : {};
    const statusCounts = (report && report.status_counts) ? report.status_counts : {};
    if (crmInstallationsPending) crmInstallationsPending.textContent = String(kpis.pending_count || 0);
    if (crmInstallationsOverdue) crmInstallationsOverdue.textContent = String(kpis.overdue_count || 0);
    if (crmInstallationsDueWeek) crmInstallationsDueWeek.textContent = String(kpis.due_this_week_count || 0);
    if (crmInstallationsCompleted) crmInstallationsCompleted.textContent = String(kpis.completed_count || 0);

    if (crmInstallationsStatusStrip) {
      crmInstallationsStatusStrip.innerHTML = '';
      const statuses = [
        'customer_paid',
        'customer_validated',
        'porta_profile_created',
        'pole_delivered',
        'equipment_assigned',
        'technician_assigned',
        'installation_completed',
        'installation_escalated',
        'customer_refunded',
      ];
      statuses.forEach((status) => {
        const count = statusCounts[status] || 0;
        const pill = document.createElement('div');
        const className = status === 'installation_completed'
          ? 'status-good'
          : (status === 'installation_escalated' || status === 'customer_refunded' ? 'status-warning' : '');
        pill.className = `crm-install-status-pill ${className}`;
        pill.innerHTML = `<span>${escapeHtml(formatCrmLabel(status))}</span><span class="count">${count}</span>`;
        crmInstallationsStatusStrip.appendChild(pill);
      });
    }

    renderInstallMonthlyChart(report && report.monthly_completed);
  }

  // Single-series monthly bar chart (installations completed per month). Inline
  // SVG so it's self-contained and theme-aware; one hue (no legend needed — the
  // card title names the series), rounded bar tops, a recessive baseline, and a
  // per-bar hover tooltip via <title>.
  function renderInstallMonthlyChart(points) {
    const el = document.getElementById('crmInstallationsMonthlyChart');
    if (!el) return;
    const data = Array.isArray(points) ? points : [];
    if (!data.length) {
      el.innerHTML = '<div class="muted" style="font-size:12px;">No data.</div>';
      return;
    }
    const W = 360, H = 140, padL = 4, padR = 4, padTop = 16, padBottom = 18;
    const plotW = W - padL - padR;
    const plotH = H - padTop - padBottom;
    const n = data.length;
    const max = Math.max(1, ...data.map((d) => Number(d.count) || 0));
    const gap = 4;
    const barW = (plotW - gap * (n - 1)) / n;
    const baseY = padTop + plotH;

    const marks = data.map((d, i) => {
      const count = Number(d.count) || 0;
      const x = padL + i * (barW + gap);
      const cx = x + barW / 2;
      const h = count > 0 ? Math.max(2, (count / max) * plotH) : 0;
      const tip = `${escapeHtml(d.label || '')} ${escapeHtml((d.month || '').slice(0, 4))}: ${count}`;
      const bar = h > 0
        ? `<rect class="bar" x="${x.toFixed(1)}" y="${(baseY - h).toFixed(1)}" width="${barW.toFixed(1)}" height="${h.toFixed(1)}" rx="3" ry="3"><title>${tip}</title></rect>`
        : `<rect x="${x.toFixed(1)}" y="${(baseY - 2).toFixed(1)}" width="${barW.toFixed(1)}" height="2" rx="1" fill="rgba(148,163,184,0.35)"><title>${tip}</title></rect>`;
      const value = count > 0
        ? `<text x="${cx.toFixed(1)}" y="${(baseY - h - 3).toFixed(1)}" text-anchor="middle" font-size="8" fill="var(--sub)">${count}</text>`
        : '';
      const label = `<text x="${cx.toFixed(1)}" y="${(H - 5)}" text-anchor="middle" font-size="8" fill="var(--sub)">${escapeHtml(d.label || '')}</text>`;
      return value + bar + label;
    }).join('');

    const baseline = `<line x1="${padL}" y1="${baseY}" x2="${W - padR}" y2="${baseY}" stroke="var(--border-soft, rgba(148,163,184,0.35))" stroke-width="1"/>`;
    el.innerHTML = `<svg viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Installations completed per month, last 12 months">${baseline}${marks}</svg>`;
  }

  function updateCrmInstallationsPagination() {
    if (crmInstallationsPageInfo) {
      const total = Number(crmInstallationsState.total || 0);
      const count = crmInstallationsState.items.length || 0;
      const offset = crmInstallationsState.offset || 0;
      if (!total) {
        crmInstallationsPageInfo.textContent = crmInstallationsState.search ? 'No results' : 'No installations';
      } else {
        const start = offset + 1;
        const end = offset + count;
        const page = Math.floor(offset / CRM_INSTALL_PAGE_SIZE) + 1;
        const totalPages = Math.max(1, Math.ceil(total / CRM_INSTALL_PAGE_SIZE));
        crmInstallationsPageInfo.textContent = `Showing ${start}-${end} of ${total} • Page ${page} / ${totalPages}`;
      }
    }
    const disablePrev = crmInstallationsState.offset <= 0 || crmInstallationsState.loading;
    const disableNext = (crmInstallationsState.offset + CRM_INSTALL_PAGE_SIZE) >= (crmInstallationsState.total || 0) || crmInstallationsState.loading;
    if (crmInstallationsPrev) crmInstallationsPrev.disabled = disablePrev;
    if (crmInstallationsNext) crmInstallationsNext.disabled = disableNext;
  }

  function renderCrmInstallationsTable(items) {
    if (!crmInstallationsTbody) return;
    // Move the detail panel back home before wiping the tbody (it may be living in
    // an inline expander row); remember which row was open so we can re-expand it.
    const reopenId = crmInstallationsState.openRowId;
    closeInlineInstallDetail();
    crmInstallationsTbody.innerHTML = '';
    if (!items.length) {
      crmInstallationsTbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">No installations found.</td></tr>';
      updateCrmInstallationsPagination();
      return;
    }
    items.forEach((item, index) => {
      const row = document.createElement('tr');
      const rowNumber = crmInstallationsState.offset + index + 1;
      const age = deriveInstallationAge(item.paid_at);
      const expected = item.expected_install_date ? formatDateTimeShort(item.expected_install_date) : '—';
      const contact = item.contact_phone || (Array.isArray(item.phone_numbers) ? item.phone_numbers[0] : '') || '—';
      const name = item.contact_name || item.address || item.id;
      const flagged = Boolean(item.flagged_at || item.flag_reason);
      row.innerHTML = `
        <td><input type="checkbox" class="crm-install-select" data-id="${escapeHtml(item.id)}" ${crmInstallationsState.selectedIds.has(item.id) ? 'checked' : ''}></td>
        <td>${rowNumber}</td>
        <td>
          <div>
            <strong>${escapeHtml(name || '—')}</strong>
            ${flagged ? '<span class="crm-install-flag-badge">Flagged</span>' : ''}
          </div>
          <div style="font-size:11px; color:var(--sub);">${escapeHtml(item.customer_type || '')} · ${escapeHtml(item.technology_type || '')}</div>
        </td>
        <td>${formatInstallationStatus(item.status)}</td>
        <td>${escapeHtml(contact)}</td>
        <td>${escapeHtml(expected)}</td>
        <td>${age != null ? `${age}d` : '—'}</td>
        <td>${item.ticket_short_code ? `#${escapeHtml(item.ticket_short_code)}` : (item.ticket_id ? `#${escapeHtml(String(item.ticket_id).slice(0, 8))}` : '—')}</td>
        <td><button class="btn" data-install-open="${escapeHtml(item.id)}">Open</button></td>
      `;
      if (flagged) row.classList.add('crm-install-row-flagged');
      crmInstallationsTbody.appendChild(row);
    });
    // Re-expand the previously open row (e.g. after a save-triggered refresh),
    // reusing the already fetched detail when it's the same installation.
    if (reopenId && items.some((it) => it.id === reopenId)) {
      const btn = crmInstallationsTbody.querySelector(`button[data-install-open="${reopenId}"]`);
      if (btn) {
        const cached = crmInstallationsState.selectedDetail && crmInstallationsState.selectedDetail.id === reopenId;
        openInlineInstallDetail(reopenId, btn, { skipLoad: cached }).catch(() => {});
      }
    }
    updateCrmInstallationsPagination();
  }

  // Collapse any inline-expanded detail: move the shared panel back to its hidden
  // home, drop the expander row, and reset all Open/Close buttons.
  function closeInlineInstallDetail() {
    if (crmInstallationDetail) {
      crmInstallationDetail.hidden = true;
      if (crmInstallDetailHome && crmInstallationDetail.parentNode !== crmInstallDetailHome) {
        crmInstallDetailHome.appendChild(crmInstallationDetail);
      }
    }
    if (crmInstallationsTbody) {
      const inline = crmInstallationsTbody.querySelector('tr.crm-install-inline-detail-row');
      if (inline) inline.remove();
      crmInstallationsTbody.querySelectorAll('button[data-install-open]').forEach((b) => {
        b.textContent = 'Open';
        b.classList.remove('primary');
      });
    }
    crmInstallationsState.openRowId = null;
  }

  // Expand the detail panel inline, in an expander row right under `openBtn`'s row.
  // Clicking the same row again toggles it closed. `skipLoad` reuses the already
  // fetched detail (used when re-opening after a list refresh).
  async function openInlineInstallDetail(id, openBtn, { skipLoad = false } = {}) {
    const toggling = crmInstallationsState.openRowId === id;
    closeInlineInstallDetail();
    if (toggling) return;
    const row = openBtn && openBtn.closest('tr');
    if (!row || !crmInstallationDetail) return;
    const tr = document.createElement('tr');
    tr.className = 'crm-install-inline-detail-row';
    const td = document.createElement('td');
    td.colSpan = 9;
    td.className = 'crm-install-inline-detail-cell';
    td.style.cssText = 'padding:0 12px 12px; background:var(--panel-2, rgba(120,120,120,0.05));';
    tr.appendChild(td);
    row.after(tr);
    td.appendChild(crmInstallationDetail);
    crmInstallationDetail.hidden = false;
    openBtn.textContent = 'Close';
    openBtn.classList.add('primary');
    crmInstallationsState.openRowId = id;
    if (!skipLoad) await loadCrmInstallationDetail(id);
  }

  // A small trail showing who created and who validated the installation (with
  // dates). The validated segment only appears once the install has been validated.
  function renderInstallationBreadcrumb(installation) {
    if (!crmInstallationDetailBreadcrumb) return;
    const nameOf = (username, id) => username || (id != null ? `User ${id}` : '');
    const segment = (verb, who, when) => {
      const byPart = who ? ` by <strong>${escapeHtml(who)}</strong>` : '';
      const atPart = when ? ` <span class="crumb-when">${escapeHtml(when)}</span>` : '';
      return `<span class="crumb">${verb}${byPart}${atPart}</span>`;
    };
    const segs = [];
    const createdBy = nameOf(installation.created_by_username, installation.created_by_user_id);
    const createdAt = installation.created_at ? formatDateTimeShort(installation.created_at) : '';
    if (createdBy || createdAt) segs.push(segment('Created', createdBy, createdAt));
    if (installation.validated_at) {
      const validatedBy = nameOf(installation.validated_by_username, installation.validated_by_user_id);
      segs.push(segment('Validated', validatedBy, formatDateTimeShort(installation.validated_at)));
    }
    crmInstallationDetailBreadcrumb.innerHTML = segs.join('<span class="crumb-sep">›</span>');
    crmInstallationDetailBreadcrumb.hidden = segs.length === 0;
  }

  function renderInstallationDetail(installation) {
    if (!crmInstallationDetailBody || !crmInstallationDetailEmpty) return;
    if (!installation) {
      crmInstallationDetailEmpty.hidden = false;
      crmInstallationDetailBody.hidden = true;
      return;
    }
    crmInstallationDetailEmpty.hidden = true;
    crmInstallationDetailBody.hidden = false;
    crmInstallationDetailName.innerHTML = '';
    const nameSpan = document.createElement('span');
    nameSpan.textContent = installation.contact_name || installation.address || installation.id;
    crmInstallationDetailName.appendChild(nameSpan);
    if (installation.flagged_at || installation.flag_reason) {
      const badge = document.createElement('span');
      badge.className = 'crm-install-flag-badge';
      badge.textContent = 'Flagged';
      crmInstallationDetailName.appendChild(badge);
    }
    crmInstallationDetailMeta.textContent = `${formatCrmLabel(installation.status)} · ${formatCrmLabel(installation.customer_type)} · ${formatCrmLabel(installation.technology_type)}`;
    renderInstallationBreadcrumb(installation);
    crmInstallationDetailGrid.innerHTML = '';
    const equipment = Array.isArray(installation.equipment) ? installation.equipment[0] : null;
    const simValue = equipment?.sim_card_number || '—';
    const iccidValue = resolveIccidTail(equipment) || '____';
    const fields = [
      ['Status', formatCrmLabel(installation.status)],
      ['Customer type', formatCrmLabel(installation.customer_type)],
      ['Technology', formatCrmLabel(installation.technology_type)],
      ['Installation type', formatCrmLabel(installation.installation_type)],
      ['Expected date', installation.expected_install_date ? formatDateTimeShort(installation.expected_install_date) : '—'],
      ['Paid at', installation.paid_at ? formatDateTimeShort(installation.paid_at) : '—'],
      ['Address', installation.address || '—'],
      ['Phone', installation.contact_phone || '—'],
      ['Email', installation.contact_email || '—'],
      ['Package', installation.package_code ? (getCrmPlanLabel(installation.package_code, installation.customer_type) || installation.package_code) : '—'],
      ['Ticket', installation.ticket_short_code ? `#${installation.ticket_short_code}` : (installation.ticket_id ? `#${String(installation.ticket_id).slice(0, 8)}` : '—')],
      ['Ticket status', installation.ticket_status ? formatCrmLabel(installation.ticket_status) : '—'],
      ['Technician', installation.technician_name || '—'],
      ['SIM', simValue],
      ['SIM ICCID (last 4)', iccidValue],
    ];
    fields.forEach(([label, value]) => {
      const card = document.createElement('div');
      card.className = 'crm-install-field';
      card.innerHTML = `<div class="label">${escapeHtml(label)}</div><div class="value">${escapeHtml(String(value || '—'))}</div>`;
      crmInstallationDetailGrid.appendChild(card);
    });
    crmInstallationPayments.innerHTML = '';
    const payments = Array.isArray(installation.payments) ? installation.payments : [];
    if (!payments.length) {
      crmInstallationPayments.innerHTML = '<div style="color:var(--sub);">No payments recorded.</div>';
    } else {
      const isFirstMonthPayment = (pay) => {
        const items = Array.isArray(pay.items) ? pay.items : [];
        return items.some((item) => {
          const name = item && item.name ? String(item.name).toLowerCase() : '';
          return name.includes('first month subscription');
        });
      };
      const initialPayments = payments.filter((pay) => pay.payment_type === 'initial');
      const subscriptionPayments = payments.filter((pay) => pay.payment_type === 'supplementary' && isFirstMonthPayment(pay));
      const extraPayments = payments.filter((pay) => pay.payment_type === 'supplementary' && !isFirstMonthPayment(pay));

      const renderPaymentBubble = (title, items) => {
        const bubble = document.createElement('div');
        bubble.className = 'crm-install-payment-bubble';
        const header = document.createElement('div');
        header.className = 'crm-install-payment-title';
        header.textContent = title;
        bubble.appendChild(header);
        if (!items.length) {
          const empty = document.createElement('div');
          empty.className = 'crm-install-payment-empty';
          empty.textContent = 'None';
          bubble.appendChild(empty);
          return bubble;
        }
        const list = document.createElement('ul');
        list.className = 'crm-install-payment-items';
        items.forEach((item) => {
          const li = document.createElement('li');
          li.innerHTML = `${escapeHtml(item.label)} · <strong>${formatCurrency(item.amount)}</strong>${item.meta ? ` · ${escapeHtml(item.meta)}` : ''}`;
          list.appendChild(li);
        });
        bubble.appendChild(list);
        return bubble;
      };

      const initialItems = initialPayments.map((pay) => {
        const paidAt = pay.paid_at || pay.created_at || '';
        const date = paidAt ? formatDate(paidAt) : '';
        const receipt = pay.receipt_ref ? `Receipt ${pay.receipt_ref}` : '';
        const meta = [date, receipt].filter(Boolean).join(' • ');
        return { label: 'Installation', amount: pay.amount, meta };
      });
      const subscriptionItems = subscriptionPayments.map((pay) => {
        const paidAt = pay.paid_at || pay.created_at || '';
        const date = paidAt ? formatDate(paidAt) : '';
        const receipt = pay.receipt_ref ? `Receipt ${pay.receipt_ref}` : '';
        const meta = [date, receipt].filter(Boolean).join(' • ');
        return { label: 'First month subscription', amount: pay.amount, meta };
      });
      const extraItems = extraPayments.flatMap((pay) => {
        const paidAt = pay.paid_at || pay.created_at || '';
        const date = paidAt ? formatDate(paidAt) : '';
        const receipt = pay.receipt_ref ? `Receipt ${pay.receipt_ref}` : '';
        const meta = [date, receipt].filter(Boolean).join(' • ');
        const items = Array.isArray(pay.items) && pay.items.length
          ? pay.items
          : [{ name: 'Additional payment' }];
        return items.map((entry) => ({
          label: entry && entry.name ? String(entry.name) : 'Additional payment',
          amount: pay.amount,
          meta,
        }));
      });

      crmInstallationPayments.appendChild(renderPaymentBubble('Installation payment', initialItems));
      crmInstallationPayments.appendChild(renderPaymentBubble('First month subscription', subscriptionItems));
      if (extraItems.length) {
        crmInstallationPayments.appendChild(renderPaymentBubble('Additional payments', extraItems));
      }
    }
    if (crmInstallationFlagDetails && crmInstallationFlagDetailsBody) {
      crmInstallationFlagDetailsBody.innerHTML = '';
      const flaggedAt = installation.flagged_at ? formatDateTimeShort(installation.flagged_at) : '';
      const flaggedBy = installation.flagged_by_username
        || (installation.flagged_by_user_id != null ? `User ${installation.flagged_by_user_id}` : '');
      const reason = installation.flag_reason || '';
      const hasFlag = Boolean(flaggedAt || reason || flaggedBy);
      crmInstallationFlagDetails.hidden = !hasFlag;
      if (hasFlag) {
        const bubble = document.createElement('div');
        bubble.className = 'crm-install-payment-bubble';
        const header = document.createElement('div');
        header.className = 'crm-install-payment-title';
        header.textContent = 'Flag details';
        bubble.appendChild(header);
        const list = document.createElement('div');
        list.className = 'crm-install-flag-details';
        const rows = [
          ['Reason', reason || '—'],
          ['Flagged at', flaggedAt || '—'],
          ['Flagged by', flaggedBy || '—'],
        ];
        rows.forEach(([label, value]) => {
          const row = document.createElement('div');
          row.className = 'crm-install-flag-row';
          row.innerHTML = `<span class="label">${escapeHtml(label)}</span><span>${escapeHtml(String(value || '—'))}</span>`;
          list.appendChild(row);
        });
        bubble.appendChild(list);
        crmInstallationFlagDetailsBody.appendChild(bubble);
      }
    }
    const ticketAction = installation.ticket_id ? '<button class="btn" data-install-action="view_ticket">View ticket</button>' : '';
    const ticketAllowedStates = new Set([
      'equipment_assigned',
      'technician_assigned',
      'porta_profile_created',
      'installation_completed',
      'installation_escalated',
    ]);
    const canCreateTicket = installation.status && ticketAllowedStates.has(installation.status);
    const createTicketAction = installation.ticket_id || !canCreateTicket
      ? ''
      : '<button class="btn" data-install-action="ticket">Create ticket</button>';
    const isCustomerPaid = installation.status === 'customer_paid';
    const isCancelled = installation.status === 'installation_cancelled';
    const editablePreTechStatuses = new Set([
      'customer_validated',
      'pole_delivered',
      'equipment_assigned',
    ]);
    const canRecordAfterValidation = installation.status && editablePreTechStatuses.has(installation.status);
    const validateAction = isCustomerPaid
      ? '<button class="btn" data-install-action="validate">Validate</button>'
      : '';
    const revertValidationAction = installation.status === 'customer_validated' && hasRole('root', 'customer_care_supervisor')
      ? '<button class="btn warn" data-install-action="revert_validation">Revert validation</button>'
      : '';
    const detailsAction = '<button class="btn ghost" data-install-action="details">Details</button>';
    const paymentAction = isCustomerPaid
      ? '<button class="btn" data-install-action="payment">Add payment</button>'
      : '';
    const poleAction = canRecordAfterValidation
      ? '<button class="btn" data-install-action="pole">Pole delivery</button>'
      : '';
    const equipmentAssigned = hasAssignedEquipment(installation);
    // Backfill: let the operator assign equipment on an install that was
    // provisioned without it (status stays; the backend keeps the later state).
    const equipmentBackfillStates = new Set([
      'technician_assigned',
      'porta_profile_created',
      'installation_completed',
      'installation_escalated',
    ]);
    const canBackfillEquipment = !equipmentAssigned
      && installation.status
      && equipmentBackfillStates.has(installation.status);
    const equipmentAction = (canRecordAfterValidation || canBackfillEquipment)
      ? `<button class="btn" data-install-action="equipment">${equipmentAssigned ? 'Assign equipment' : 'Assign equipment ⚠'}</button>`
      : '';
    const deleteAction = hasRole('root', 'installation_admin')
      ? '<button class="btn danger" data-install-action="delete">Delete</button>'
      : '';
    const isCompleted = installation.status === 'installation_completed';
    const canAutoProvision = !isCompleted || hasRole('root');
    // Guard rail: block auto-provisioning until equipment is assigned.
    const autoProvisionAction = canAutoProvision
      ? `<button class="btn warn" data-install-action="auto"${equipmentAssigned ? '' : ' disabled title="Assign equipment before auto-provisioning."'}>Auto-provision</button>`
      : '';
    const canReclose = hasRole('root')
      && installation.status === 'porta_profile_created'
      && String(installation.ticket_status || '').toLowerCase() === 'closed';
    const recloseAction = canReclose
      ? '<button class="btn warn" data-install-action="reclose">Reclose installation</button>'
      : '';
    const isRefunded = installation.status === 'customer_refunded';
    const refundDisabled = installation.status === 'installation_cancelled';
    let refundAction = '';
    if (isRefunded) {
      refundAction = hasRole('root')
        ? '<button class="btn warn" data-install-action="unrefund">Unrefund</button>'
        : '';
    } else if (hasRole('root', 'installation_admin', 'inventory_supervisor', 'billing_officer', 'billing_supervisor')) {
      refundAction = `<button class="btn warn" data-install-action="refund"${refundDisabled ? ' disabled title="Already cancelled"' : ''}>Refund</button>`;
    }
    crmInstallationDetailActions.innerHTML = `
      ${validateAction}
      ${revertValidationAction}
      ${detailsAction}
      <button class="btn warn" data-install-action="flag">Flag</button>
      ${paymentAction}
      ${poleAction}
      ${equipmentAction}
      ${createTicketAction}
      ${autoProvisionAction}
      ${recloseAction}
      ${refundAction}
      <button class="btn" data-install-action="print_pole">Print pole sheet</button>
      <button class="btn" data-install-action="print_note">Print delivery note</button>
      ${ticketAction}
      ${deleteAction}
    `;
  }

  async function loadCrmInstallationsReport() {
    if (!crmInstallationsDashboard) {
      // eslint-disable-next-line no-console
      console.warn('crm-installations: #crmInstallationsDashboard missing — report will not load.');
      return;
    }
    try {
      const params = new URLSearchParams();
      if (crmInstallationsState.status) {
        params.set('status', crmInstallationsState.status);
      }
      if (crmInstallationsState.customerType) params.set('customer_type', crmInstallationsState.customerType);
      if (crmInstallationsState.ageMin) params.set('age_gt', crmInstallationsState.ageMin);
      if (crmInstallationsState.expectedBefore) params.set('expected_before', crmInstallationsState.expectedBefore);
      params.set('include_rows', 'false');
      params.set('limit', '0');
      const data = await adminAPI.fetch(API.crmInstallationsDashboard(params.toString()), { method: 'GET' });
      renderInstallationsDashboard(data || null);
    } catch (err) {
      renderInstallationsDashboard(null);
    }
  }

  async function loadCrmInstallations() {
    if (!crmInstallationsTbody) { console.warn('crm-installations: #crmInstallationsTbody missing — list will not load.'); return; }
    if (crmInstallationsState.loading) return;
    crmInstallationsState.loading = true;
    crmInstallationsState.selectedIds = new Set();
    if (crmInstallationsSelectAll) crmInstallationsSelectAll.checked = false;
    updateCrmInstallationsPagination();
    crmInstallationsTbody.innerHTML = '<tr><td colspan="9" style="color:var(--sub);">Loading…</td></tr>';
    try {
      const params = new URLSearchParams();
      params.set('limit', String(CRM_INSTALL_PAGE_SIZE));
      params.set('offset', String(crmInstallationsState.offset || 0));
      const term = (crmInstallationsState.search || '').trim();
      if (term) params.set('search', term);
      if (crmInstallationsState.status) {
        params.set('status', crmInstallationsState.status);
      } else if (!term) {
        // Default queue = active statuses only. When searching without an explicit
        // status, search across ALL statuses so completed/cancelled/refunded
        // installations are found too (server-side, whole table — not one page).
        [
          'customer_paid',
          'customer_validated',
          'pole_delivered',
          'equipment_assigned',
          'technician_assigned',
          'porta_profile_created',
          'installation_escalated',
        ].forEach((status) => params.append('status', status));
      }
      if (crmInstallationsState.customerType) params.set('customer_type', crmInstallationsState.customerType);
      if (crmInstallationsState.ageMin) params.set('age_gt', crmInstallationsState.ageMin);
      if (crmInstallationsState.expectedBefore) params.set('expected_before', crmInstallationsState.expectedBefore);
      const data = await adminAPI.fetch(API.crmInstallations(params.toString()), { method: 'GET' });
      const items = Array.isArray(data?.items) ? data.items : [];
      // Search is applied server-side across the whole table, so the returned page
      // and count are already the matches — no client-side filtering needed.
      crmInstallationsState.items = items;
      crmInstallationsState.total = typeof data?.count === 'number'
        ? data.count
        : (crmInstallationsState.offset + items.length);
      renderCrmInstallationsTable(items);
    } catch (err) {
      crmInstallationsState.items = [];
      crmInstallationsTbody.innerHTML = '<tr><td colspan="9" style="color:var(--err);">Failed to load installations</td></tr>';
      showFlash(err?.message || 'Failed to load installations', 'error');
    } finally {
      crmInstallationsState.loading = false;
      updateCrmInstallationsPagination();
      loadCrmInstallationsReport();
    }
  }

  // Promotions & discounts on an installation. The cashier (billing) can
  // apply/remove until the install is provisioned; after that it's read-only.
  async function renderInstallationPromos(installation) {
    const box = document.getElementById('crmInstallationPromos');
    if (!box || !installation) return;
    const id = installation.id;
    const editable = !installation.porta_created_at && hasRole('root', 'billing_officer', 'billing_supervisor');
    box.innerHTML = '<div class="muted" style="font-size:12px;">Loading…</div>';
    if (!box.dataset.wired) {
      box.dataset.wired = '1';
      box.addEventListener('click', async (event) => {
        const iid = crmInstallationsState.selectedId;
        if (!iid) return;
        const applyBtn = event.target.closest('button[data-promo-apply]');
        const removeBtn = event.target.closest('button[data-promo-remove]');
        if (applyBtn) {
          const sel = document.getElementById('crmPromoApplySelect');
          if (!sel || !sel.value) return;
          try {
            await adminAPI.fetch(API.crmInstallationPromotions(iid), { method: 'POST', body: JSON.stringify({ promotion_id: sel.value }) });
            showFlash('Promotion applied');
            loadCrmInstallationDetail(iid);
          } catch (err) { showFlash(err?.message || 'Failed to apply promotion', 'error'); }
        } else if (removeBtn) {
          try {
            await adminAPI.fetch(API.crmInstallationPromotion(iid, removeBtn.dataset.promoRemove), { method: 'DELETE' });
            showFlash('Promotion removed');
            loadCrmInstallationDetail(iid);
          } catch (err) { showFlash(err?.message || 'Failed to remove promotion', 'error'); }
        }
      });
    }
    try {
      const [applied, cat] = await Promise.all([
        adminAPI.fetch(API.crmInstallationPromotions(id), { method: 'GET' }),
        adminAPI.fetch(API.crmPromotions('active_only=true'), { method: 'GET' }).catch(() => ({ items: [] })),
      ]);
      const grants = Array.isArray(applied) ? applied : [];
      const active = Array.isArray(cat?.items) ? cat.items : [];
      const appliedIds = new Set(grants.map((g) => g.promotion_id));
      const rows = grants.map((g) => {
        const shape = g.discount_type === 'full_waiver' ? 'waive install'
          : (g.discount_type === 'percent' ? `${g.discount_value}% off` : `${g.discount_value} off`);
        const rm = editable ? ` <button class="btn xs" data-promo-remove="${escapeHtml(g.id)}" title="Remove">✕</button>` : '';
        return `<div class="row" style="justify-content:space-between; gap:8px; font-size:12px; padding:2px 0;">
          <span>${escapeHtml(g.promo_name)} <span class="muted">(${escapeHtml(shape)})</span></span>
          <span>−${escapeHtml(String(g.discount_amount))}${rm}</span></div>`;
      }).join('') || '<div class="muted" style="font-size:12px;">None applied.</div>';
      let control = '';
      if (editable) {
        const opts = active.filter((p) => !appliedIds.has(p.id))
          .map((p) => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.code)} — ${escapeHtml(p.name)}</option>`).join('');
        control = opts
          ? `<div class="row" style="gap:6px; margin-top:6px;"><select id="crmPromoApplySelect" style="flex:1; min-width:0;">${opts}</select><button class="btn xs primary" data-promo-apply="1">Apply</button></div>`
          : '<div class="muted" style="font-size:11px; margin-top:6px;">No more promotions to apply.</div>';
      } else if (installation.porta_created_at) {
        control = '<div class="muted" style="font-size:11px; margin-top:6px;">Locked — installation provisioned.</div>';
      }
      box.innerHTML = rows + control;
    } catch (err) {
      box.innerHTML = `<div class="muted" style="font-size:12px;">${escapeHtml(err?.message || 'Failed to load promotions')}</div>`;
    }
  }

  async function loadCrmInstallationDetail(id) {
    try {
      const data = await adminAPI.fetch(API.crmInstallation(id), { method: 'GET' });
      crmInstallationsState.selectedId = id;
      crmInstallationsState.selectedDetail = data;
      if (data?.package_code) {
        try {
          await ensureCrmPlanCache(data.customer_type || '');
        } catch (err) {
          showFlash(err?.message || 'Failed to load plan names', 'error');
        }
      }
      renderInstallationDetail(data);
      renderInstallationPromos(data);
    } catch (err) {
      showFlash(err?.message || 'Failed to load installation', 'error');
    }
  }

  async function createInstallationTicket(assigneeUserId) {
    const id = crmInstallationsState.selectedId;
    if (!id) return;
    try {
      await adminAPI.fetch(API.crmInstallationTicket(id), { method: 'POST', body: JSON.stringify({ assignee_user_id: assigneeUserId }) });
      showFlash('Ticket created');
      await loadCrmInstallationDetail(id);
      loadCrmInstallations();
    } catch (err) {
      const message = err?.message === 'installation_ticket_exists'
        ? 'A ticket already exists for this installation.'
        : (err?.message === 'active_ticket_exists'
          ? 'An active ticket already exists for this customer.'
          : (err?.message || 'Failed to create ticket'));
      showFlash(message, 'error');
    }
  }

  function printInstallationSheet(mode = 'pole') {
    const id = crmInstallationsState.selectedId;
    const installation = crmInstallationsState.items.find((item) => item.id === id);
    if (!installation) return;
    const detail = crmInstallationsState.selectedDetail && crmInstallationsState.selectedDetail.id === id
      ? crmInstallationsState.selectedDetail
      : installation;
    openPrintWindow([detail], mode);
  }

  function printBulkSheets(mode) {
    const selected = crmInstallationsState.items.filter((item) => crmInstallationsState.selectedIds.has(item.id));
    if (!selected.length) {
      showFlash('Select at least one installation', 'error');
      return;
    }
    openPrintWindow(selected, mode);
  }

  function openRefundVoucher(installation, reason, totalPaid) {
    const title = 'Installation Refund Voucher';
    const name = installation?.contact_name || installation?.address || installation?.id || 'Customer';
    const paid = Number(totalPaid || 0);
    const now = new Date();
    const date = Number.isNaN(now.getTime()) ? '' : formatDate(now);
    const voucher = `
      <div style="border:1px solid #cbd5f5; border-radius:10px; padding:16px;">
        <div style="font-weight:700; font-size:18px;">${escapeHtml(title)}</div>
        <div style="margin-top:8px; font-size:12px; color:#475569;">Date: ${escapeHtml(date)}</div>
        <div style="margin-top:12px; font-size:14px;">
          <div><strong>Customer:</strong> ${escapeHtml(name)}</div>
          <div><strong>Total paid:</strong> ${formatCurrency(paid)}</div>
          <div style="margin-top:8px;"><strong>Reason:</strong> ${escapeHtml(reason || '—')}</div>
        </div>
        <div style="margin-top:18px; display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:14px;">
          <div>CEO: ________________________</div>
          <div>Finance Director: ________________________</div>
          <div>Sales Manager: ________________________</div>
          <div>Customer Care Manager: ________________________</div>
        </div>
      </div>
    `;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    win.document.write(`
      <html><head><title>${title}</title></head>
      <body style="font-family:Arial, sans-serif; padding:20px;">
        ${voucher}
        <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  }

  function formatPoleInfo(item) {
    const deliveries = Array.isArray(item.pole_deliveries) ? item.pole_deliveries : [];
    let latest = null;
    if (deliveries.length) {
      latest = deliveries.reduce((acc, current) => {
        if (!acc) return current;
        const accTime = acc.delivered_at ? Date.parse(acc.delivered_at) : 0;
        const curTime = current.delivered_at ? Date.parse(current.delivered_at) : 0;
        return curTime >= accTime ? current : acc;
      }, null);
    }
    const poleSize = latest && latest.pole_size ? String(latest.pole_size) : '';
    const mounting = latest && latest.mounting_material ? formatCrmLabel(latest.mounting_material) : '';
    const parts = [];
    if (poleSize) parts.push(`Pole height: ${poleSize}`);
    if (mounting) parts.push(`Mounting: ${mounting}`);
    return parts.join(' · ');
  }

  // Whether an installation has real equipment assigned (SIM / router / CPE /
  // accessories) — the gate for auto-provisioning and the backfill affordance.
  function hasAssignedEquipment(detail) {
    const eq = detail && Array.isArray(detail.equipment) ? detail.equipment[0] : null;
    if (!eq) return false;
    const core = eq.router_model || eq.ont_model || eq.cpe_model || eq.sim_card_number
      || eq.sim_iccid || eq.iccid || eq.iccid_number;
    const accessories = Array.isArray(eq.accessories) ? eq.accessories.filter((a) => a && a.name) : [];
    return Boolean(core) || accessories.length > 0;
  }

  function formatEquipmentInfo(item) {
    const equipment = Array.isArray(item.equipment) ? item.equipment[0] : null;
    if (!equipment) return '';
    const parts = [];
    if (equipment.ont_model) parts.push(`ONT: ${equipment.ont_model}`);
    if (equipment.router_model) parts.push(`Router: ${equipment.router_model}`);
    if (equipment.cpe_model) parts.push(`CPE: ${equipment.cpe_model}`);
    if (equipment.sim_card_number) parts.push(`SIM: ${equipment.sim_card_number}`);
    const accessories = Array.isArray(equipment.accessories) ? equipment.accessories : [];
    if (accessories.length) {
      const list = accessories
        .map((acc) => {
          const name = acc && acc.name ? String(acc.name) : '';
          const qty = acc && acc.qty != null ? Number(acc.qty) : null;
          if (name && qty != null) return `${name} × ${qty}`;
          return name || '';
        })
        .filter(Boolean)
        .join(', ');
      if (list) parts.push(`Accessories: ${list}`);
    }
    return parts.join(' · ');
  }

  function formatMaterialsList(item) {
    const materials = [];
    const equipment = Array.isArray(item.equipment) ? item.equipment[0] : null;
    const tech = (item && item.technology_type ? String(item.technology_type) : '').toLowerCase();
    const iccidValue = resolveIccidTail(equipment);
    if (equipment && equipment.sim_card_number) {
      materials.push(`SIM card: ${equipment.sim_card_number}`);
    }
    if (equipment || tech === '4g' || tech === 'lte') {
      materials.push(`SIM ICCID (last 4): ${iccidValue || '____'}`);
    }
    if (equipment && equipment.router_model) {
      materials.push(`Router: ${equipment.router_model}`);
    }
    if (equipment && equipment.cpe_model) {
      materials.push(`CPE: ${equipment.cpe_model}`);
    }
    const accessPointCount = item && item.access_point_count != null ? Number(item.access_point_count) : 0;
    if (!Number.isNaN(accessPointCount) && accessPointCount > 0) {
      materials.push(`Additional access points x ${accessPointCount}`);
    }
    const accessories = equipment && Array.isArray(equipment.accessories) ? equipment.accessories : [];
    accessories.forEach((acc) => {
      const name = acc && acc.name ? String(acc.name).trim() : '';
      const qty = acc && acc.qty != null ? Number(acc.qty) : null;
      if (!name) return;
      if (qty != null && !Number.isNaN(qty)) {
        materials.push(`${name} x ${qty}`);
      } else {
        materials.push(name);
      }
    });
    const payments = Array.isArray(item.payments) ? item.payments : [];
    payments.forEach((pay) => {
      if (pay.payment_type !== 'supplementary') return;
      const items = Array.isArray(pay.items) ? pay.items : [];
      items.forEach((entry) => {
        const name = entry && entry.name ? String(entry.name).trim() : '';
        if (name) materials.push(name);
      });
    });
    const unique = Array.from(new Set(materials));
    if (!unique.length) return '';
    const list = unique.map((mat) => `<li>${escapeHtml(mat)}</li>`).join('');
    return `<ul style="margin:4px 0 0; padding-left:18px;">${list}</ul>`;
  }

  function openPrintWindow(items, mode) {
    const title = mode === 'note' ? 'Delivery Notes' : 'Pole Delivery Sheets';
    const rows = items.map((item) => {
      const poleDetails = mode === 'pole' ? formatPoleInfo(item) : '';
      const equipmentDetails = mode === 'pole' ? formatEquipmentInfo(item) : '';
      const packageLabel = item.package_label || item.package_name || item.package_display || item.package_code || '';
      const materialsSection = mode === 'pole'
        ? `
          <div style="border:1px solid #cbd5f5; border-radius:8px; padding:8px; font-size:12px;">
            <div style="font-weight:600; margin-bottom:6px;">Materials delivered</div>
            <div style="display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap:6px;">
              <label><input type="checkbox" /> Full bracket</label>
              <label><input type="checkbox" /> Male bracket only</label>
              <label><input type="checkbox" /> Clip only</label>
              <label><input type="checkbox" /> Pole 2m</label>
              <label><input type="checkbox" /> Pole 4m</label>
              <label><input type="checkbox" /> Pole 6m</label>
            </div>
          </div>`
        : '';
      const deliveredMaterials = mode === 'note'
        ? `<div style="font-size:12px; color:#475569; margin-top:6px;"><strong>Materials delivered</strong>${formatMaterialsList(item)}</div>`
        : '';
      const card = `
        <div style="border:1px solid #cbd5f5; border-radius:10px; padding:12px; page-break-inside: avoid;">
          <div style="display:grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); gap:10px;">
            <div>
              <div style="font-weight:700; margin-bottom:6px;">${escapeHtml(item.contact_name || 'Installation')}</div>
              <div style="font-size:12px; color:#475569;">${escapeHtml(item.address || '')}</div>
              <div style="font-size:12px; color:#475569;">Phone: ${escapeHtml(item.contact_phone || '')}</div>
              ${deliveredMaterials}
              <div style="font-size:12px; color:#475569;">Package: ${escapeHtml(packageLabel)}</div>
              ${poleDetails ? `<div style="font-size:12px; color:#475569;">${escapeHtml(poleDetails)}</div>` : ''}
              ${equipmentDetails ? `<div style="font-size:12px; color:#475569;">${escapeHtml(equipmentDetails)}</div>` : ''}
            </div>
            <div>
              ${materialsSection}
            </div>
          </div>
          <div style="margin-top:8px; font-size:12px;">Signature (Customer): ______________________</div>
          <div style="margin-top:8px; font-size:12px;">Signature (Admin Manager): ______________________</div>
          <div style="margin-top:8px; font-size:12px;">Signature (Driver/Tech): ______________________</div>
        </div>`;
      if (mode === 'note') {
        return `
          <div class="note-page">
            <div class="note-header">${title}</div>
            <div class="note-pair">
              <div class="note-card">${card}</div>
              <div class="note-cut"></div>
              <div class="note-card">${card}</div>
            </div>
          </div>`;
      }
      return `<div style="margin-bottom:12px;">${card}</div>`;
    }).join('');
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    const layoutStyles = mode === 'note'
      ? `
        <style>
          @page { size: A4; margin: 12mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          .note-page {
            display: flex;
            flex-direction: column;
            height: calc(297mm - 24mm);
            break-after: page;
          }
          .note-page:last-child { break-after: auto; }
          .note-header { margin: 0 0 4mm; font-size: 16px; font-weight: 700; }
          .note-pair {
            display: grid;
            grid-template-rows: 1fr auto 1fr;
            gap: 8mm;
            flex: 1;
          }
          .note-cut { border-top: 2px dashed #94a3b8; height: 0; }
        </style>
      `
      : '';
    const content = rows;
    const bodyPadding = mode === 'note' ? '0' : '20px';
    const titleMarkup = mode === 'note' ? '' : `<h2 class="${mode === 'note' ? 'note-title' : ''}">${title}</h2>`;
    win.document.write(`
      <html><head><title>${title}</title>${layoutStyles}</head>
      <body style="font-family:Arial, sans-serif; padding:${bodyPadding};">
        ${titleMarkup}
        ${content}
        <script>window.print();</script>
      </body></html>
    `);
    win.document.close();
  }

  async function loadTechnicianRoster() {
    const state = getTechnicianRosterState() || {};
    if (state.loaded || state.loading || !crmAssignTechnicianSelect) return;
    setTechnicianRosterState({ loaded: state.loaded, loading: true });
    try {
      const data = await adminAPI.fetch(API.adminTechnicians, { method: 'GET' });
      const items = Array.isArray(data?.technicians) ? data.technicians : (Array.isArray(data) ? data : []);
      crmAssignTechnicianSelect.innerHTML = '<option value="">Select technician</option>';
      items.forEach((tech) => {
        const option = document.createElement('option');
        option.value = tech.id;
        option.textContent = tech.display_name || tech.name || `Tech ${tech.id}`;
        crmAssignTechnicianSelect.appendChild(option);
      });
      setTechnicianRosterState({ loaded: true, loading: false });
    } catch (err) {
      setTechnicianRosterState({ loaded: state.loaded, loading: false });
      showFlash(err?.message || 'Failed to load technicians', 'error');
    }
  }

  function initCrmInstallationsView() {
    resolveDom();
    if (crmInstallationsInitialized) {
      loadCrmInstallations();
      return;
    }
    crmInstallationsInitialized = true;
    renderInstallationDetail(null);
    if (crmInstallationsNewBtn) {
      crmInstallationsNewBtn.style.display = hasRole('root','billing_officer','billing_supervisor') ? '' : 'none';
    }
    if (crmInstallationsOpenReports) {
      crmInstallationsOpenReports.style.display = hasRole('root','sales_officer','sales_supervisor','billing_officer','billing_supervisor','inventory_supervisor','installation_admin','field_tech_supervisor','customer_care_supervisor','customer_care') ? '' : 'none';
    }
    if (crmInstallationsBulkPole) {
      crmInstallationsBulkPole.style.display = hasRole('root','installation_admin','inventory_supervisor') ? '' : 'none';
    }
    if (crmInstallationsBulkNote) {
      crmInstallationsBulkNote.style.display = hasRole('root','installation_admin','inventory_supervisor') ? '' : 'none';
    }
    if (crmInstallationsBulkDispatch) {
      crmInstallationsBulkDispatch.style.display = hasRole('root','installation_admin','inventory_supervisor') ? '' : 'none';
    }
    renderCrmAlerts(window.crmBlockingAlerts || []);
    if (crmInstallationsSearch) {
      let searchDebounce = null;
      crmInstallationsSearch.addEventListener('input', () => {
        const next = crmInstallationsSearch.value.trim();
        if (next === (crmInstallationsState.search || '')) return;
        crmInstallationsState.search = next;
        crmInstallationsState.offset = 0;   // new term → back to the first page
        if (searchDebounce) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => loadCrmInstallations(), 300);
      });
    }
    if (crmInstallationsStatusFilter) {
      crmInstallationsStatusFilter.addEventListener('change', () => {
        crmInstallationsState.status = crmInstallationsStatusFilter.value;
        crmInstallationsState.offset = 0;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsCustomerFilter) {
      crmInstallationsCustomerFilter.addEventListener('change', () => {
        crmInstallationsState.customerType = crmInstallationsCustomerFilter.value;
        crmInstallationsState.offset = 0;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsAgeFilter) {
      crmInstallationsAgeFilter.addEventListener('change', () => {
        crmInstallationsState.ageMin = crmInstallationsAgeFilter.value.trim();
        crmInstallationsState.offset = 0;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsExpectedFilter) {
      crmInstallationsExpectedFilter.addEventListener('change', () => {
        crmInstallationsState.expectedBefore = crmInstallationsExpectedFilter.value;
        crmInstallationsState.offset = 0;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsPrev) {
      crmInstallationsPrev.addEventListener('click', () => {
        if (crmInstallationsState.loading) return;
        const nextOffset = Math.max(0, (crmInstallationsState.offset || 0) - CRM_INSTALL_PAGE_SIZE);
        if (nextOffset === crmInstallationsState.offset) return;
        crmInstallationsState.offset = nextOffset;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsNext) {
      crmInstallationsNext.addEventListener('click', () => {
        if (crmInstallationsState.loading) return;
        const total = crmInstallationsState.total || 0;
        const nextOffset = (crmInstallationsState.offset || 0) + CRM_INSTALL_PAGE_SIZE;
        if (nextOffset >= total) return;
        crmInstallationsState.offset = nextOffset;
        loadCrmInstallations();
      });
    }
    if (crmInstallationsNewBtn) {
      crmInstallationsNewBtn.addEventListener('click', () => {
        if (crmInstallationForm) crmInstallationForm.reset();
        _clearInstallProspectLink();
        updateInstallOptionsForTech();
        loadCrmPlansForCustomerType(crmInstallCustomerType ? crmInstallCustomerType.value : '');
        openDialog(crmInstallationDialog);
      });
    }
    const installProspectSearchEl = document.getElementById('crmInstallProspectSearch');
    if (installProspectSearchEl) {
      installProspectSearchEl.addEventListener('input', () => {
        if (crmInstallProspectSearchTimer) clearTimeout(crmInstallProspectSearchTimer);
        crmInstallProspectSearchTimer = setTimeout(runInstallProspectSearch, 300);
      });
    }
    if (crmInstallationsOpenReports) {
      crmInstallationsOpenReports.addEventListener('click', () => {
        const params = new URLSearchParams();
        params.set('report', 'crm_installations');
        const url = `/reports/index.html?${params.toString()}`;
        window.open(url, '_blank', 'noopener');
      });
    }
    if (crmInstallationsExport) {
      crmInstallationsExport.addEventListener('click', async () => {
        try {
          const params = new URLSearchParams();
          if (crmInstallationsState.status) params.set('status', crmInstallationsState.status);
          if (crmInstallationsState.customerType) params.set('customer_type', crmInstallationsState.customerType);
          if (crmInstallationsState.ageMin) params.set('age_gt', crmInstallationsState.ageMin);
          if (crmInstallationsState.expectedBefore) params.set('expected_before', crmInstallationsState.expectedBefore);
          const data = await adminAPI.fetch(API.crmInstallationsExport(params.toString()), { method: 'GET' });
          if (data && data.job_id) {
            showFlash('Export started. You will be notified when it is ready.');
            crmInstallationsState.lastExportJobId = data.job_id;
          } else {
            showFlash('Export started.', 'info');
          }
        } catch (err) {
          let msg = err?.message || 'Failed to start export';
          if (msg === 'report_row_limit_exceeded') {
            msg = 'Too many rows. Tighten filters and try again.';
          }
          if (msg === 'report_job_limit_reached') {
            msg = 'You already have two exports running. Wait for one to finish.';
          }
          showFlash(msg, 'error');
        }
      });
    }
    if (crmInstallationsBulkPole) {
      crmInstallationsBulkPole.addEventListener('click', () => {
        printBulkSheets('pole');
      });
    }
    if (crmInstallationsBulkDispatch) {
      crmInstallationsBulkDispatch.addEventListener('click', () => {
        if (!crmInstallationsState.selectedIds.size) {
          showFlash('Select at least one installation', 'error');
          return;
        }
        crmBulkPoleDispatch = true;
        if (crmPoleBulkList) {
          const selected = crmInstallationsState.items.filter((item) => crmInstallationsState.selectedIds.has(item.id));
          renderBulkPoleForms(selected);
        }
        openDialog(crmInstallPoleDialog);
      });
    }
    if (crmInstallationsBulkNote) {
      crmInstallationsBulkNote.addEventListener('click', () => {
        printBulkSheets('note');
      });
    }
    if (crmInstallationsSelectAll) {
      crmInstallationsSelectAll.addEventListener('change', () => {
        const checked = crmInstallationsSelectAll.checked;
        crmInstallationsState.selectedIds = new Set();
        document.querySelectorAll('.crm-install-select').forEach((el) => {
          el.checked = checked;
          if (checked) crmInstallationsState.selectedIds.add(el.dataset.id);
        });
      });
    }
    if (crmInstallationsTbody) {
      crmInstallationsTbody.addEventListener('click', async (event) => {
        const openBtn = event.target.closest('button[data-install-open]');
        if (openBtn) {
          const id = openBtn.dataset.installOpen;
          await openInlineInstallDetail(id, openBtn);
          return;
        }
        const checkbox = event.target.closest('.crm-install-select');
        if (checkbox) {
          const id = checkbox.dataset.id;
          if (checkbox.checked) {
            crmInstallationsState.selectedIds.add(id);
          } else {
            crmInstallationsState.selectedIds.delete(id);
          }
        }
      });
    }
    if (crmInstallTechnology) {
      crmInstallTechnology.addEventListener('change', () => {
        updateInstallOptionsForTech();
        if (crmInstallPackage && crmInstallCustomerType) {
          const key = crmInstallCustomerType.value || 'all';
          const items = crmPlanCache.get(key) || [];
          renderCrmPlanOptions(items, crmInstallCustomerType.value, crmInstallTechnology.value);
        }
      });
    }
    [crmInstallOntTypeRouter, crmInstallOntTypeModem].forEach((radio) => {
      if (radio) radio.addEventListener('change', updateFiberOntDependents);
    });
    if (crmInstallCustomerType) {
      crmInstallCustomerType.addEventListener('change', () => {
        loadCrmPlansForCustomerType(crmInstallCustomerType.value);
        updateSubscriptionFieldVisibility(crmInstallCustomerType.value);
      });
    }
    if (crmInstallDetailsCustomerType) {
      crmInstallDetailsCustomerType.addEventListener('change', () => {
        loadCrmPlansForSelect(
          crmInstallDetailsPackage,
          crmInstallDetailsCustomerType.value,
          crmInstallDetailsTechnology ? crmInstallDetailsTechnology.value : ''
        );
      });
    }
    if (crmInstallDetailsTechnology) {
      crmInstallDetailsTechnology.addEventListener('change', () => {
        const tech = crmInstallDetailsTechnology.value;
        loadCrmPlansForSelect(
          crmInstallDetailsPackage,
          crmInstallDetailsCustomerType ? crmInstallDetailsCustomerType.value : '',
          tech
        );
        // Re-filter installation type + re-render the component picker for the new
        // technology, and surface the "equipment will reset" warning.
        _filterDetailsTypeOptions(tech, crmInstallDetailsType ? crmInstallDetailsType.value : '');
        if (crmInstallDetailsComponents && crmInstallDetailsComponents.style.display !== 'none') {
          _renderDetailsComponents(tech, {});   // fresh options for the new tech
        }
        _syncDetailsTechWarning();
      });
    }
    if (crmInstallationDetailActions) {
      crmInstallationDetailActions.addEventListener('click', async (event) => {
        const btn = event.target.closest('button[data-install-action]');
        if (!btn) return;
        const action = btn.dataset.installAction;
        if (!crmInstallationsState.selectedId) return;
        if (action === 'validate') {
          const detail = crmInstallationsState.selectedDetail;
          const customerType = detail ? detail.customer_type : '';
          const tech = detail ? detail.technology_type : '';
          loadCrmPlansForSelect(crmValidatePackage, customerType, tech).then(() => {
            if (detail?.package_code && crmValidatePackage) {
              crmValidatePackage.value = detail.package_code;
            }
          });
          prefillCrmValidateForm(detail);
          renderCrmValidateMaterials(detail);
          resetCrmValidateChecklist(detail);
          openDialog(crmInstallValidateDialog);
        }
        if (action === 'details') {
          renderCrmInstallDetails(crmInstallationsState.selectedDetail);
          openDialog(crmInstallDetailsDialog);
        }
        if (action === 'flag') openDialog(crmInstallFlagDialog);
        if (action === 'payment') {
          resetCrmPaymentForm();
          openDialog(crmInstallPaymentDialog);
        }
        if (action === 'refund') {
          if (crmInstallRefundForm) crmInstallRefundForm.reset();
          openDialog(crmInstallRefundDialog);
        }
        if (action === 'unrefund') {
          const detail = crmInstallationsState.selectedDetail;
          const label = detail?.contact_name || detail?.address || detail?.id || 'this installation';
          if (!window.confirm(`Revert refund for ${label}?`)) return;
          try {
            await adminAPI.fetch(API.crmInstallationUnrefund(crmInstallationsState.selectedId), { method: 'POST' });
            showFlash('Refund reverted');
            await loadCrmInstallationDetail(crmInstallationsState.selectedId);
            loadCrmInstallations();
          } catch (err) {
            showFlash(err?.message || 'Failed to revert refund', 'error');
          }
        }
        if (action === 'equipment') {
          resetCrmAccessoriesForm(crmInstallationsState.selectedDetail);
          openDialog(crmInstallEquipmentDialog);
        }
        if (action === 'auto') {
          if (!hasAssignedEquipment(crmInstallationsState.selectedDetail)) {
            showFlash('Assign equipment before auto-provisioning.', 'error');
            return;
          }
          renderCrmAutoProvisionPayments(crmInstallationsState.selectedDetail);
          prepareCrmAutoProvisionOverrides(crmInstallationsState.selectedDetail);
          openDialog(crmInstallAutoProvisionDialog);
        }
        if (action === 'reclose') {
          const detail = crmInstallationsState.selectedDetail;
          const label = detail?.contact_name || detail?.address || detail?.id || 'this installation';
          if (!window.confirm(`Reclose ${label}?`)) return;
          try {
            await adminAPI.fetch(API.crmInstallationReclose(crmInstallationsState.selectedId), { method: 'POST' });
            showFlash('Installation reclosed');
            await loadCrmInstallationDetail(crmInstallationsState.selectedId);
            loadCrmInstallations();
          } catch (err) {
            showFlash(err?.message || 'Failed to reclose installation', 'error');
          }
        }
        if (action === 'ticket') {
          ensureSupervisorUsersLoaded().then(() => {
            populateCrmInstallTicketSupervisors();
            openDialog(crmInstallTicketDialog);
          });
        }
        if (action === 'pole') {
          if (crmPoleBulkList) {
            const detail = crmInstallationsState.selectedDetail;
            const selected = detail ? [detail] : crmInstallationsState.items.filter((item) => item.id === crmInstallationsState.selectedId);
            renderBulkPoleForms(selected);
          }
          openDialog(crmInstallPoleDialog);
        }
        if (action === 'view_ticket') {
          const installation = crmInstallationsState.items.find((item) => item.id === crmInstallationsState.selectedId);
          if (installation && installation.ticket_id) {
            openTicketFromNotification(installation.ticket_id);
          }
        }
        if (action === 'delete') {
          const detail = crmInstallationsState.selectedDetail;
          const label = detail?.contact_name || detail?.address || detail?.id || 'this installation';
          if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
          try {
            await adminAPI.fetch(API.crmInstallation(crmInstallationsState.selectedId), { method: 'DELETE' });
            showFlash('Installation deleted');
            crmInstallationsState.selectedId = null;
            crmInstallationsState.selectedDetail = null;
            renderInstallationDetail(null);
            loadCrmInstallations();
          } catch (err) {
            showFlash(err?.message || 'Failed to delete installation', 'error');
          }
        }
        if (action === 'revert_validation') {
          const detail = crmInstallationsState.selectedDetail;
          const label = detail?.contact_name || detail?.address || detail?.id || 'this installation';
          if (!window.confirm(`Revert validation for ${label}?`)) return;
          try {
            await adminAPI.fetch(API.crmInstallationRevertValidation(crmInstallationsState.selectedId), { method: 'POST' });
            showFlash('Validation reverted');
            await loadCrmInstallationDetail(crmInstallationsState.selectedId);
            loadCrmInstallations();
          } catch (err) {
            showFlash(err?.message || 'Failed to revert validation', 'error');
          }
        }
        if (action === 'print_pole') printInstallationSheet('pole');
        if (action === 'print_note') printInstallationSheet('note');
      });
    }
    if (crmInstallationForm) {
      crmInstallationForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (crmInstallationSubmit) crmInstallationSubmit.disabled = true;
        try {
          const tech = crmInstallTechnology ? crmInstallTechnology.value : '';
          const optionErrors = [];
          if (tech === '4g' || tech === 'wimax') {
            if (!crmInstallOptCpe || !crmInstallOptCpe.checked) {
              optionErrors.push('CPE is required.');
            }
            if (!crmInstallOptCables || !crmInstallOptCables.checked) {
              optionErrors.push('Cables are required for 4G/WiMAX.');
            }
            if (!crmInstallRouterAx?.checked && !crmInstallRouterSmall?.checked && !crmInstallRouterOwned?.checked) {
              optionErrors.push('Select AX router, small router, or customer owned.');
            }
          }
          if (tech === 'fiber') {
            if (!crmInstallOptDropCable?.checked) {
              optionErrors.push('Drop cable is required for fiber.');
            }
            if (!crmInstallOptOnt?.checked) {
              optionErrors.push('ONT is required for fiber.');
            }
            const ontType = (crmInstallOntTypeModem && crmInstallOntTypeModem.checked)
              ? 'modem'
              : ((crmInstallOntTypeRouter && crmInstallOntTypeRouter.checked) ? 'router' : null);
            if (!ontType) {
              optionErrors.push('Select the ONT type (router or modem) for fiber.');
            }
            // WiFi6 router is optional (customer may already own one), so no
            // requirement here even for a modem ONT.
          }
          if (optionErrors.length) {
            showFlash(optionErrors.join(' '), 'error');
            return;
          }
          if (!crmInstallPackage || !crmInstallPackage.value) {
            showFlash('Subscription plan is required.', 'error');
            return;
          }
          const derivedInstallType = deriveInstallationTypeFromOptions();
          if (crmInstallType) crmInstallType.value = derivedInstallType;
          const primaryPhone = normalizePhoneWithCode(
            crmInstallContactPhoneCode ? crmInstallContactPhoneCode.value : '',
            crmInstallContactPhoneNumber ? crmInstallContactPhoneNumber.value : ''
          );
          if (primaryPhone.error || !primaryPhone.value) {
            showFlash(`Primary phone: ${primaryPhone.error || 'is required.'}`, 'error');
            return;
          }
          const secondaryPhone = normalizePhoneWithCode(
            crmInstallSecondaryPhoneCode ? crmInstallSecondaryPhoneCode.value : '',
            crmInstallSecondaryPhoneNumber ? crmInstallSecondaryPhoneNumber.value : '',
            { allowEmpty: true }
          );
          if (secondaryPhone.error) {
            showFlash(`Secondary phone: ${secondaryPhone.error}`, 'error');
            return;
          }
          const phoneNumbers = [];
          if (primaryPhone.value) phoneNumbers.push(primaryPhone.value);
          if (secondaryPhone.value) phoneNumbers.push(secondaryPhone.value);
          const addressLine = crmInstallAddress.value.trim();
          const city = crmInstallCity ? crmInstallCity.value.trim() : '';
          const region = crmInstallRegion ? crmInstallRegion.value.trim() : '';
          if (addressLine || city || region) {
            if (!city || !region) {
              showFlash('City and region are required when an address is provided.', 'error');
              return;
            }
          }
          const addressCheck = validateCrmInstallAddress(addressLine, city, region);
          if (addressCheck.error) {
            showFlash(addressCheck.error, 'error');
            return;
          }
          const addressParts = addressCheck.parts;
          const subscriptionAmount = crmInstallAmountSubscription && crmInstallAmountSubscription.value
            ? Number(crmInstallAmountSubscription.value)
            : null;
          const installReceipt = crmInstallReceiptInstall ? crmInstallReceiptInstall.value.trim() : '';
          const subscriptionReceipt = crmInstallReceiptSubscription ? crmInstallReceiptSubscription.value.trim() : '';
          // A first subscription payment is REQUIRED for Home/SME (prepaid) and
          // OPTIONAL for corporate (postpaid) — corporate may still choose to pay
          // one, so we no longer reject it here.
          if (crmInstallCustomerType.value !== 'corporate' && !subscriptionAmount) {
            showFlash('First subscription payment amount is required for Home/SME.', 'error');
            return;
          }
          const payload = {
            customer_type: crmInstallCustomerType.value,
            technology_type: tech,
            installation_type: derivedInstallType,
            package_code: crmInstallPackage.value.trim() || null,
            amount_paid: crmInstallAmountInstall.value ? Number(crmInstallAmountInstall.value) : null,
            receipt_ref: installReceipt || null,
            access_point_count: crmInstallAccessPoints.value ? Number(crmInstallAccessPoints.value) : 0,
            contact_name: crmInstallContactName.value.trim(),
            contact_phone: primaryPhone.value || null,
            contact_email: crmInstallContactEmail.value.trim() || null,
            address: addressParts.length ? addressCheck.address : null,
            notes: crmInstallNotes.value.trim() || null,
            phone_numbers: phoneNumbers.length ? phoneNumbers : null,
            prospect_id: crmInstallSelectedProspectId || null,
            metadata: {
              installation_options: collectInstallationOptions(),
              address_parts: addressParts.length ? {
                address: addressLine || null,
                city: city || null,
                region: region || null,
                country: "Gambia",
              } : null,
            },
          };
          if (!payload.contact_name) {
            showFlash('Customer name is required.', 'error');
            return;
          }
          if (!payload.contact_phone) {
            showFlash('Primary phone is required.', 'error');
            return;
          }
          const portaName = `${payload.contact_name} ${payload.contact_phone}`.trim();
          if (portaName.length > 41) {
            showFlash(`Customer name is too long for billing — name + phone must be ≤ 41 characters (currently ${portaName.length}). Please shorten the name.`, 'error');
            return;
          }
          const created = await adminAPI.fetch(API.crmInstallations(), { method: 'POST', body: JSON.stringify(payload) });
          if (subscriptionAmount && created?.id) {
            await adminAPI.fetch(API.crmInstallationPayments(created.id), {
              method: 'POST',
              body: JSON.stringify({
                amount: subscriptionAmount,
                payment_type: 'supplementary',
                receipt_ref: subscriptionReceipt || null,
                items: [{ name: 'First month subscription', qty: 1 }],
              }),
            });
          }
          showFlash('Installation created');
          closeDialog(crmInstallationDialog);
          crmInstallationsState.offset = 0;
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to create installation', 'error');
        } finally {
          if (crmInstallationSubmit) crmInstallationSubmit.disabled = false;
        }
      });
    }
    if (crmInstallCustomerType) {
      updateSubscriptionFieldVisibility(crmInstallCustomerType.value);
    }
    if (crmInstallValidateForm) {
      crmInstallValidateForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        if (!crmValidateChecklistComplete()) {
          showFlash('Complete the confirmation checklist before validating.', 'error');
          syncCrmValidateChecklist();
          return;
        }
        const firstName = crmValidateFirstName ? crmValidateFirstName.value.trim() : '';
        const lastName = crmValidateLastName ? crmValidateLastName.value.trim() : '';
        const contactName = [firstName, lastName].filter(Boolean).join(' ').trim() || null;
        const street = crmValidateStreet ? crmValidateStreet.value.trim() : '';
        const city = crmValidateCity ? crmValidateCity.value.trim() : '';
        const region = crmValidateRegion ? crmValidateRegion.value.trim() : '';
        const addressCheck = validateCrmInstallAddress(street, city, region);
        if (addressCheck.error) {
          showFlash(addressCheck.error, 'error');
          return;
        }
        const addressParts = addressCheck.parts;
        const validatePhone = crmValidatePhone.value.trim() || null;
        if (contactName) {
          const portaName = `${contactName} ${validatePhone || ''}`.trim();
          if (portaName.length > 41) {
            showFlash(`Customer name is too long for billing — name + phone must be ≤ 41 characters (currently ${portaName.length}). Please shorten the name.`, 'error');
            return;
          }
        }
        const payload = {
          contact_name: contactName,
          contact_phone: validatePhone,
          contact_email: crmValidateEmail.value.trim() || null,
          address: addressParts.length ? addressCheck.address : null,
          expected_install_date: crmValidateExpected.value || null,
          package_code: crmValidatePackage.value ? crmValidatePackage.value.trim() : null,
        };
        try {
          await adminAPI.fetch(API.crmInstallationValidate(id), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Customer validated');
          closeDialog(crmInstallValidateDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Validation failed', 'error');
        }
      });
    }
    if (crmInstallFlagForm) {
      crmInstallFlagForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const payload = { reason: crmFlagReason.value.trim() };
        if (!payload.reason) {
          showFlash('Flag reason is required', 'error');
          return;
        }
        try {
          await adminAPI.fetch(API.crmInstallationFlag(id), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Installation flagged');
          closeDialog(crmInstallFlagDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to flag', 'error');
        }
      });
    }
    if (crmInstallRefundForm) {
      crmInstallRefundForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const detail = crmInstallationsState.selectedDetail;
        const reason = crmInstallRefundReason ? crmInstallRefundReason.value.trim() : '';
        if (!reason) {
          showFlash('Refund reason is required', 'error');
          return;
        }
        const confirmed = window.confirm('Confirm refund for this installation?');
        if (!confirmed) return;
        try {
          await adminAPI.fetch(API.crmInstallationRefund(id), { method: 'POST', body: JSON.stringify({ reason }) });
          showFlash('Installation refunded');
          closeDialog(crmInstallRefundDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
          const printDetail = crmInstallationsState.selectedDetail || detail;
          const payments = Array.isArray(printDetail?.payments) ? printDetail.payments : [];
          const totalPaid = payments.reduce((sum, pay) => sum + Number(pay.amount || 0), 0);
          openRefundVoucher(printDetail, reason, totalPaid);
        } catch (err) {
          showFlash(err?.message || 'Failed to refund installation', 'error');
        }
      });
    }
    if (crmInstallPaymentForm) {
      crmInstallPaymentForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const { items, error } = collectCrmPaymentItems();
        if (error) {
          showFlash(error, 'error');
          return;
        }
        const payload = {
          amount: crmPaymentAmount.value ? Number(crmPaymentAmount.value) : 0,
          receipt_ref: crmPaymentReceipt.value.trim() || null,
          items,
        };
        if (!payload.amount) {
          showFlash('Amount is required', 'error');
          return;
        }
        try {
          await adminAPI.fetch(API.crmInstallationPayments(id), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Payment recorded');
          closeDialog(crmInstallPaymentDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to add payment', 'error');
        }
      });
    }
    if (crmPaymentAddItem) {
      crmPaymentAddItem.addEventListener('click', () => addCrmPaymentItemRow());
    }
    if (crmInstallPoleForm) {
      crmInstallPoleForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        try {
          const bulkItems = crmPoleBulkList ? Array.from(crmPoleBulkList.querySelectorAll('.crm-pole-bulk-item')) : [];
          const requests = bulkItems.map((row) => {
            const id = row.dataset.installationId;
            const payload = {
              delivery_sheet_number: row.querySelector('[data-field="sheet"]')?.value.trim() || null,
              pole_size: row.querySelector('[data-field="size"]')?.value || 'none',
              mounting_material: row.querySelector('[data-field="mounting"]')?.value || 'full_bracket',
              driver_name: row.querySelector('[data-field="driver"]')?.value.trim() || null,
              delivered_at: row.querySelector('[data-field="delivered"]')?.value || null,
            };
            return adminAPI.fetch(API.crmInstallationPole(id), { method: 'POST', body: JSON.stringify(payload) });
          });
          await Promise.all(requests);
          showFlash('Pole deliveries recorded');
          crmBulkPoleDispatch = false;
          closeDialog(crmInstallPoleDialog);
          if (bulkItems.length === 1) {
            const id = bulkItems[0].dataset.installationId;
            if (id) await loadCrmInstallationDetail(id);
          }
          loadCrmInstallations();
          if (bulkItems.length > 1) {
            printBulkSheets('pole');
          } else {
            printInstallationSheet('pole');
          }
        } catch (err) {
          showFlash(err?.message || 'Failed to record pole delivery', 'error');
        }
      });
    }
    if (crmInstallPoleDialog) {
      crmInstallPoleDialog.addEventListener('close', () => {
        crmBulkPoleDispatch = false;
        if (crmPoleBulkList) {
          crmPoleBulkList.innerHTML = '';
        }
      });
    }
    if (crmInstallEquipmentForm) {
      crmInstallEquipmentForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const detail = crmInstallationsState.selectedDetail;
        const requiresSim = installationRequiresSim(detail);
        if (requiresSim) {
          const simUpdate = updateCrmSimFromIccidTail();
          if (simUpdate.error) {
            showFlash(simUpdate.error, 'error');
            return;
          }
        }
        const simCardNumber = crmEquipSim.value.trim();
        const simIccid = crmEquipIccidTail ? crmEquipIccidTail.value.trim() : '';
        const cpeModel = crmEquipCpe.value.trim();
        // A disabled picker means the customer owns the router: nothing to issue.
        const routerSupplied = !crmEquipRouter.disabled;
        const routerModel = routerSupplied ? crmEquipRouter.value.trim() : '';
        const isFiberEquip = (detail?.technology_type || '').toString().toLowerCase() === 'fiber';
        // The ONT (fiber only) — the device handed over; a Router-ONT is the router too.
        const ontModel = isFiberEquip && crmEquipOnt ? crmEquipOnt.value.trim() : '';
        // CPE applies to wireless installs only, not fiber.
        const cpeRequired = !isFiberEquip;
        if (cpeRequired && !cpeModel) {
          showFlash('CPE model is required.', 'error');
          return;
        }
        if (requiresSim && !simCardNumber) {
          showFlash('SIM card number is required.', 'error');
          return;
        }
        if (requiresSim && !simIccid) {
          showFlash('SIM ICCID last 4 digits are required.', 'error');
          return;
        }
        if (installationRequiresRouter(detail) && !routerModel) {
          showFlash('Router model is required for this installation type.', 'error');
          return;
        }
        // Inventory-only: a router/ONT/CPE, when given, must be a catalogued item.
        const routerItem = resolveEquipItem(routerModel);
        const ontItem = resolveEquipItem(ontModel);
        const cpeItem = resolveEquipItem(cpeModel);
        if (routerModel && !routerItem) {
          showFlash('Pick the router from inventory — free-text models are no longer allowed.', 'error');
          return;
        }
        if (ontModel && !ontItem) {
          showFlash('Pick the ONT from inventory — free-text models are no longer allowed.', 'error');
          return;
        }
        if (cpeModel && !cpeItem) {
          showFlash('Pick the CPE from inventory — free-text models are no longer allowed.', 'error');
          return;
        }
        const { items: accessories, error } = collectCrmAccessories();
        if (error) {
          showFlash(error, 'error');
          return;
        }
        // Picked SKUs feed the installation's stock request as real reservations;
        // the stored model becomes the item's clean catalogue name.
        const payload = {
          sim_card_number: simCardNumber || null,
          sim_iccid: simIccid || null,
          // '' (not null) when the customer owns it, so an older stored router is cleared.
          router_model: routerSupplied ? ((routerItem ? routerItem.name : routerModel) || null) : '',
          ont_model: isFiberEquip ? ((ontItem ? ontItem.name : ontModel) || null) : null,
          cpe_model: (cpeItem ? cpeItem.name : cpeModel) || null,
          router_sku: routerItem ? routerItem.sku : null,
          ont_sku: ontItem ? ontItem.sku : null,
          cpe_sku: cpeItem ? cpeItem.sku : null,
          accessories,
        };
        try {
          await adminAPI.fetch(API.crmInstallationEquipment(id), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Equipment assigned');
          closeDialog(crmInstallEquipmentDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to assign equipment', 'error');
        }
      });
    }
    if (crmInstallDetailsForm) {
      crmInstallDetailsForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!hasRole('root', 'inventory_supervisor')) {
          closeDialog(crmInstallDetailsDialog);
          return;
        }
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        // Product/component fields are sent only while the picker is editable (the
        // component container is visible). Otherwise we PATCH contact/address only,
        // so the backend product guard is never triggered for locked installs.
        const productEditable = !!(crmInstallDetailsComponents && crmInstallDetailsComponents.style.display !== 'none');
        const customerType = crmInstallDetailsCustomerType ? crmInstallDetailsCustomerType.value : '';
        const tech = crmInstallDetailsTechnology ? crmInstallDetailsTechnology.value : '';
        const installType = crmInstallDetailsType ? crmInstallDetailsType.value : '';
        const packageCode = crmInstallDetailsPackage ? crmInstallDetailsPackage.value.trim() : '';
        const expectedDate = crmInstallDetailsExpected ? crmInstallDetailsExpected.value : '';
        const accessPointsRaw = crmInstallDetailsAccessPoints ? crmInstallDetailsAccessPoints.value.trim() : '';
        const accessPoints = accessPointsRaw ? Number(accessPointsRaw) : null;
        if (accessPointsRaw && (Number.isNaN(accessPoints) || accessPoints < 0)) {
          showFlash('Additional access points must be 0 or more.', 'error');
          return;
        }
        const primaryPhone = normalizePhoneWithCode(
          crmInstallDetailsContactPhoneCode ? crmInstallDetailsContactPhoneCode.value : '',
          crmInstallDetailsContactPhoneNumber ? crmInstallDetailsContactPhoneNumber.value : '',
          { allowEmpty: true }
        );
        if (primaryPhone.error) {
          showFlash(`Primary phone: ${primaryPhone.error}`, 'error');
          return;
        }
        const secondaryPhone = normalizePhoneWithCode(
          crmInstallDetailsSecondaryPhoneCode ? crmInstallDetailsSecondaryPhoneCode.value : '',
          crmInstallDetailsSecondaryPhoneNumber ? crmInstallDetailsSecondaryPhoneNumber.value : '',
          { allowEmpty: true }
        );
        if (secondaryPhone.error) {
          showFlash(`Secondary phone: ${secondaryPhone.error}`, 'error');
          return;
        }
        const phoneNumbers = [];
        if (primaryPhone.value) phoneNumbers.push(primaryPhone.value);
        if (secondaryPhone.value && secondaryPhone.value !== primaryPhone.value) {
          phoneNumbers.push(secondaryPhone.value);
        }
        const street = crmInstallDetailsStreet ? crmInstallDetailsStreet.value.trim() : '';
        const city = crmInstallDetailsCity ? crmInstallDetailsCity.value.trim() : '';
        const region = crmInstallDetailsRegion ? crmInstallDetailsRegion.value.trim() : '';
        const addressCheck = validateCrmInstallAddress(street, city, region);
        if (addressCheck.error) {
          showFlash(addressCheck.error, 'error');
          return;
        }
        const addressParts = addressCheck.parts;
        if (addressParts.length && (!city || !region)) {
          showFlash('City and region are required when an address is provided.', 'error');
          return;
        }
        const payload = {
          customer_type: customerType || null,
          notes: crmInstallDetailsNotes ? crmInstallDetailsNotes.value.trim() || null : null,
          address: addressParts.length ? addressCheck.address : null,
          phone_numbers: phoneNumbers.length ? phoneNumbers : null,
          contact_name: crmInstallDetailsContactName ? crmInstallDetailsContactName.value.trim() || null : null,
          contact_phone: primaryPhone.value || null,
          contact_email: crmInstallDetailsContactEmail ? crmInstallDetailsContactEmail.value.trim() || null : null,
          expected_install_date: expectedDate || null,
        };
        // Technology/type/plan/components + their guard/cascade only when in-window.
        if (productEditable) {
          payload.technology_type = tech || null;
          payload.installation_type = installType || null;
          payload.access_point_count = accessPointsRaw ? accessPoints : null;
          payload.package_code = packageCode || null;
          const collected = _collectDetailsComponents();
          if (collected) payload.installation_options = collected;
        }
        if (addressParts.length) {
          payload.metadata = {
            address_parts: {
              address: street || null,
              city: city || null,
              region: region || null,
              country: 'Gambia',
            },
          };
        }
        if (crmInstallDetailsSave) crmInstallDetailsSave.disabled = true;
        try {
          await adminAPI.fetch(API.crmInstallation(id), { method: 'PATCH', body: JSON.stringify(payload) });
          showFlash('Installation updated');
          closeDialog(crmInstallDetailsDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to update installation', 'error');
        } finally {
          if (crmInstallDetailsSave) crmInstallDetailsSave.disabled = false;
        }
      });
    }
    if (crmEquipIccidTail) {
      crmEquipIccidTail.addEventListener('input', () => {
        updateCrmSimFromIccidTail();
      });
    }
    if (crmInstallTicketForm) {
      crmInstallTicketForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const supervisorId = crmInstallTicketSupervisor ? crmInstallTicketSupervisor.value : '';
        if (!supervisorId) {
          showFlash('Select a supervisor', 'error');
          return;
        }
        await createInstallationTicket(supervisorId);
        closeDialog(crmInstallTicketDialog);
      });
    }
    if (crmEquipAddAccessory) {
      crmEquipAddAccessory.addEventListener('click', () => addCrmAccessoryRow());
    }
    if (crmInstallAssignTechForm) {
      crmInstallAssignTechForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const technicianId = crmAssignTechnicianSelect.value;
        if (!technicianId) {
          showFlash('Select a technician', 'error');
          return;
        }
        try {
          await adminAPI.fetch(API.crmInstallationAssignTech(id), { method: 'POST', body: JSON.stringify({ technician_id: technicianId }) });
          showFlash('Technician assigned');
          closeDialog(crmInstallAssignTechDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Failed to assign technician', 'error');
        }
      });
    }
    if (crmInstallAutoProvisionForm) {
      crmInstallAutoProvisionForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const id = crmInstallationsState.selectedId;
        if (!id) return;
        const detail = crmInstallationsState.selectedDetail;
        if (!hasAssignedEquipment(detail)) {
          showFlash('Assign equipment before auto-provisioning.', 'error');
          return;
        }
        const tech = (detail?.technology_type || '').toString().toLowerCase();
        const isFiberEmail = tech === 'fiber' || tech === 'wimax' || tech === 'ptp';
        const is4g = tech === '4g' || tech === 'lte';
        const payload = {};
        if (isFiberEmail && crmAutoEmail) {
          const emailValue = crmAutoEmail.value.trim();
          if (!emailValue) {
            showFlash('Customer email is required for this technology.', 'error');
            return;
          }
          if (emailValue.includes(',')) {
            showFlash('Use a single email address (no commas).', 'error');
            return;
          }
          payload.contact_email = emailValue;
        }
        if (is4g && crmAutoSim && crmAutoSim.value.trim()) {
          payload.sim_card_number = crmAutoSim.value.trim();
        }
        try {
          await adminAPI.fetch(API.crmInstallationAutoProvision(id), { method: 'POST', body: JSON.stringify(payload) });
          showFlash('Auto-provision completed');
          closeDialog(crmInstallAutoProvisionDialog);
          await loadCrmInstallationDetail(id);
          loadCrmInstallations();
        } catch (err) {
          showFlash(err?.message || 'Auto-provision failed', 'error');
        }
      });
    }
    if (crmAutoDidFetch) {
      crmAutoDidFetch.addEventListener('click', async (event) => {
        event.preventDefault();
        await fetchCrmAutoDidNumber();
      });
    }
    loadCrmInstallations();
    loadTechnicianRoster();
  }

  function mount() {
    window.setCrmBlockingAlerts = (alerts = []) => {
      resolveDom();
      renderCrmAlerts(Array.isArray(alerts) ? alerts : []);
    };
  }

export default {
  mount,
  init: initCrmInstallationsView,
  getState: () => crmInstallationsState,
  getPlanCache: () => crmPlanCache,
  getCrmPlanLabel,
  ensureCrmPlanCache,
};
